import {
  DateIcon,
  DeleteIcoGb,
  DownloadIcoGb,
  InfoIcoDgk,
  ModalCloseIco,
  PlusIcoDGK,
  PlusIcoGb,
  SearchIcoDgk,
} from "@modules/CustomIcons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Pagination,
  message,
} from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextArea from "antd/lib/input/TextArea";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Avans,
  ConvertBulk,
  GetCustomerCountries,
  GetCustomerCurrencies,
  GetInfByGBNO,
  GetInfByPinCode,
  YGB,
} from "../../services/Transfers/dgk";
import SelectField from "components/SelectField";
import { setDgkData } from "store/actions/DGK";
import "./index.scss";

type SelectOption = { label: string; value: string };

type YgbPage = {
  key: string;
  gbNo: string;
  serverData: any;
  docNoOptions: { label: string; value: string }[];
  formValues: any;
};

type AktPage = {
  key: string;
  formValues: any;
};

export interface ConvertData {
  toConvert: string;
  beneficiaryName?: string;
  transferAmount: number;
  amountCurrency: [{ currency: string; amount: number }];
}

const toCountryOptions = (countriesMap: Record<string, any>): SelectOption[] =>
  Object.entries(countriesMap || {}).map(([id, c]) => ({
    value: String(id),
    label: c?.name ?? String(id),
  }));

// The transferred-amount currency is not free choice: every kind offers the
// account currency and the currency the transfer is being sent in, and YGB adds
// the invoice currency that came back from customs. Builds that short list,
// de-duplicated and in the order given (account currency first).
const toFixedCurrencyOptions = (...currencies: Array<string | null | undefined>): SelectOption[] => {
  const seen: Set<string> = new Set();
  const options: SelectOption[] = [];
  currencies.forEach((raw) => {
    const cur = String(raw ?? "").trim().toUpperCase();
    if (!cur || seen.has(cur)) return;
    seen.add(cur);
    options.push({ value: cur, label: cur });
  });
  return options;
};

// True when the value is one of the currencies the field currently offers.
// Used to leave a deliberate pick alone while still repairing a stale one.
const isAllowedCurrency = (options: SelectOption[], value: any) => {
  const cur = String(value ?? "").trim().toUpperCase();
  return !!cur && options.some((o) => o.value === cur);
};

const findCountryIdByName = (
  countriesMap: Record<string, any>,
  name?: string
) => {
  if (!name) return undefined;
  const entry = Object.entries(countriesMap || {}).find(
    ([, c]) =>
      String(c?.name || "").toLowerCase() === String(name).toLowerCase()
  );
  return entry ? String(entry[0]) : undefined;
};

const normalizeDecimal = (v: any) => {
  const s = String(v ?? "").trim().replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const sanitizeDecimalInput = (value: any, maxInteger = 9, maxFraction = 2) => {
  if (value === null || value === undefined) return "";
  let s = String(value);
  s = s.replace(/[^\d.,]/g, "");
  s = s.replace(/,/g, ".");
  const firstDotIndex = s.indexOf(".");
  if (firstDotIndex !== -1) {
    const integerPart = s.slice(0, firstDotIndex);
    const fractionPart = s.slice(firstDotIndex + 1).replace(/\./g, "");
    s = `${integerPart}.${fractionPart}`;
  }
  const [integerPartRaw = "", fractionPartRaw = ""] = s.split(".");
  const integerPart = integerPartRaw.slice(0, maxInteger);
  const fractionPart = fractionPartRaw.slice(0, maxFraction);
  if (s.includes(".")) return `${integerPart}.${fractionPart}`;
  return integerPart;
};

const isDecimalString = (v: any) => {
  const s = String(v ?? "").trim().replace(",", ".");
  return /^(\d+)(\.\d{1,2})?$/.test(s);
};

const isMoney9x2 = (v: any) =>
  /^(\d{1,9})(\.\d{1,2})?$/.test(String(v ?? "").trim().replace(",", "."));

const isEnglishText = (v: any) => {
  const s = String(v ?? "");
  return /^[A-Za-z0-9\s.,\-'"\/():]+$/.test(s);
};

const hasForbiddenSymbols = (v: any) => {
  const s = String(v ?? "");
  const forbidden = /[~!@№#&;$%^*=\{\}\[\]_<>""\\|?/«»\/]/;
  return forbidden.test(s);
};

const sanitizeDgkText = (e: any) => {
  const raw = e?.target?.value ?? "";
  return String(raw).replace(/[^A-Za-z0-9\s.,\-'():]/g, "");
};

const isSameCurrency = (selected?: string, base?: string) =>
  !!selected && !!base && String(selected).trim() === String(base).trim();

const pickDgkApiMessage = (source: any): string => {
  if (!source) return "";
  const body = source.data ?? source;
  const first = body?.messages?.[0];
  return String(first?.MESSAGE || first?.message || body?.message || "").trim();
};

const isDgkSearchUnavailableError = (source: any): boolean => {
  if (Number(source?.status) !== 500) return false;
  const msg = pickDgkApiMessage(source);
  if (!msg) return false;
  return /DGK/i.test(msg) && /Ödəniş şərti|Digər|Payment terms|Other|Условие оплаты|Другое/i.test(msg);
};

const downloadBase64Pdf = (base64?: string, fileName = "advance.pdf") => {
  if (!base64) return;
  try {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error("PDF download failed:", e);
  }
};

const DgkModal: React.FC<any> = (props: any) => {
  const dispatch = useDispatch();
  const dgkData = useSelector((state: any) => state.dgk?.data);
  const { t } = useTranslation();

  const [ygbPages, setYgbPages] = useState<YgbPage[]>([]);
  const [ygbPageIndex, setYgbPageIndex] = useState(1);

  const [aktPages, setAktPages] = useState<AktPage[]>([
    {
      key: `akt-${Date.now()}`,
      formValues: {
        actExchange: "",
        actExchangeAmountInAccount: null,
      },
    },
  ]);
  const [aktPageIndex, setAktPageIndex] = useState(1);

  const [getInfoForm] = Form.useForm();
  const [getInfoFormAvans] = Form.useForm();

  const [ygbInfoForm] = Form.useForm();
  const [ygbForm] = Form.useForm();

  const [aktMainForm] = Form.useForm();
  const [aktExtraForm] = Form.useForm();

  const [avansForm] = Form.useForm();
  const avansWatchedValues = Form.useWatch([], avansForm);
  const aktServiceProviderValue = Form.useWatch("actServiceProvider", aktExtraForm);
  const [countriesMap, setCountriesMap] = useState<Record<string, any>>({});
  const [currenciesMap, setCurrenciesMap] = useState<Record<string, any>>({});

  const countryOptions = useMemo(() => toCountryOptions(countriesMap), [countriesMap]);
  const [countrySearch, setCountrySearch] = useState("");

  const filteredCountryOptions = useMemo(() => {
  const search = countrySearch.trim().toLowerCase();

  if (!search) return countryOptions;

  return countryOptions.filter((country) =>
    country.label.toLowerCase().includes(search)
  );
  }, [countryOptions, countrySearch]);

  const handleSearchCountry = (value: string) => {
  setCountrySearch(value);
  };
  // const currencyOptions = useMemo(() => toCurrencyOptions(currenciesMap), [currenciesMap]);

  const [searchGbLoading, setSearchGbLoading] = useState(false);
  const [dgkErrorMessage, setDgkErrorMessage] = useState<string | null>(null);
  const [openYgbRest, setOpenYgbRest] = useState(false);
  const [openGbPayments, setOpenGbPayments] = useState(false);

  const [openAvansRest, setOpenAvansRest] = useState(false);
  const [openAvansContract, setOpenAvansContract] = useState(false);
  const [openAvansGbList, setOpenAvansGbList] = useState(false);

  const [avansData, setAvansData] = useState<any>();
  const [avansContracts, setAvansContracts] = useState<any[]>([]);

  // Cancel-confirmation modal ("Diqqət!"). Holds the action to run on confirm,
  // or null when closed. Shared by handleCancelWithConfirm and deleteCurrentAvans.
  const [cancelConfirmAction, setCancelConfirmAction] = useState<(() => void) | null>(null);
  const openCancelConfirm = (onConfirm: () => void) => setCancelConfirmAction(() => onConfirm);
  const closeCancelConfirm = () => setCancelConfirmAction(null);

  const [ygbPaymentsPage, setYgbPaymentsPage] = useState(1);
  const [avansPage, setAvansPage] = useState(1);
  const formsPerPage = 1;

  const [saveEnabled, setSaveEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const suppressAutoConvertRef = useRef(false);

  const ygbConvertTimer = useRef<any>(null);
  const aktConvertTimer = useRef<any>(null);
  const avansConvertTimer = useRef<any>(null);

  const ygbConvertRequestId = useRef(0);
  const aktConvertRequestId = useRef(0);
  const avansConvertRequestId = useRef(0);

  const currentYgbPage = ygbPages[ygbPageIndex - 1];
  const currentAktPage = aktPages[aktPageIndex - 1];
  const activeYgbData = currentYgbPage?.serverData;
  const activeDocNoOptions = currentYgbPage?.docNoOptions || [];

  const ygbDetailsTotal = activeYgbData?.details?.length || 0;
  const ygbPaymentStart = (ygbPaymentsPage - 1) * formsPerPage;
  const ygbPaymentEnd = ygbPaymentStart + formsPerPage;
  const currentPayments = activeYgbData?.details?.slice(ygbPaymentStart, ygbPaymentEnd) || [];

  const avansDetailsTotal = avansContracts?.length || 0;
  const avansStart = (avansPage - 1) * formsPerPage;
  const avansEnd = avansStart + formsPerPage;
  const currentAvansForms = avansContracts?.slice(avansStart, avansEnd) || [];

  const toConvert = String(props?.toConvert ?? "").trim();
  const transferAmount = Number(props?.transferAmount ?? 0);
  // The currency the transfer itself is being sent in. It only differs from the
  // account currency on a USD account, where the amount currency is a free
  // choice (NOK, JPY, ...); every other account sends in its own currency and
  // this collapses back into toConvert.
  const transferCurrency = String(props?.transferCurrency ?? "").trim();

  const avansAccountNoForDisplay = (selectedCurrency: any, advanceAccountNo: any) => {
    const selected = String(selectedCurrency ?? "").trim().toUpperCase();
    const accountCurrency = String(toConvert ?? "").trim().toUpperCase();
    const currency = selected || accountCurrency;
    const fromAdvance = String(advanceAccountNo ?? "");
    if (!currency || currency === "AZN") return fromAdvance;
    const custAcc = String(props?.iban ?? "").trim();
    return custAcc || fromAdvance;
  };

  // YGB: account currency + the transfer currency + the invoice currency of the
  // GB shown on this page.
  const ygbCurrencyOptions = useMemo(
    () => toFixedCurrencyOptions(toConvert, transferCurrency, currentYgbPage?.serverData?.invoiceCurrency),
    [toConvert, transferCurrency, currentYgbPage]
  );

  // AKT and Avans: account currency, plus the transfer currency when it differs.
  // With a single option left the field is rendered disabled, as before.
  const aktCurrencyOptions = useMemo(
    () => toFixedCurrencyOptions(toConvert, transferCurrency),
    [toConvert, transferCurrency]
  );

  const avansCurrencyOptions = useMemo(
    () => toFixedCurrencyOptions(toConvert, transferCurrency),
    [toConvert, transferCurrency]
  );

  const [ygbConvertStatus, setYgbConvertStatus] = useState<Record<string, "idle" | "loading" | "success" | "error">>({});
  const [aktConvertStatus, setAktConvertStatus] = useState<Record<string, "idle" | "loading" | "success" | "error">>({});
  const [avansConvertStatus, setAvansConvertStatus] = useState<Record<number, "idle" | "loading" | "success" | "error">>({});

  useEffect(() => {
    return () => {
      clearTimeout(ygbConvertTimer.current);
      clearTimeout(aktConvertTimer.current);
      clearTimeout(avansConvertTimer.current);
    };
  }, []);

  const ensureLookupsLoaded = async () => {
    if (
      Object.keys(countriesMap || {}).length &&
      Object.keys(currenciesMap || {}).length
    ) {
      return { countries: countriesMap, currencies: currenciesMap };
    }
    const [countriesRes, currenciesRes] = await Promise.all([
      GetCustomerCountries(),
      GetCustomerCurrencies(),
    ]);
    const countries = countriesRes || {};
    const currencies = currenciesRes || {};
    setCountriesMap(countries);
    setCurrenciesMap(currencies);
    return { countries, currencies };
  };

  const buildNextYgbPages = (formValues: any, pageIndexOverride?: number) => {
    const index = typeof pageIndexOverride === "number" ? pageIndexOverride : ygbPageIndex - 1;
    return ygbPages.map((p, idx) => (idx === index ? { ...p, formValues } : p));
  };

  const getScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
    let current = element?.parentElement || null;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") &&
        current.scrollHeight > current.clientHeight;
      if (isScrollable) return current;
      current = current.parentElement;
    }
    return document.querySelector(".dgkModal .ant-modal-body") as HTMLElement | null;
  };

  const preserveModalScroll = (fieldName: string, cb: () => void) => {
    const fieldNode = document.querySelector(`[id$="${fieldName}"]`) as HTMLElement | null;
    const scroller = getScrollableParent(fieldNode);
    const prevTop = scroller?.scrollTop ?? 0;
    cb();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scroller) scroller.scrollTop = prevTop;
      });
    });
  };

  const buildNextAktPages = (formValues: any, pageIndexOverride?: number) => {
    const index = typeof pageIndexOverride === "number" ? pageIndexOverride : aktPageIndex - 1;
    return aktPages.map((p, idx) => (idx === index ? { ...p, formValues } : p));
  };

  const getCurrentYgbMergedValuesWithDerived = () => ({
    ...(currentYgbPage?.formValues || {}),
    ...ygbInfoForm.getFieldsValue(true),
    ...ygbForm.getFieldsValue(true),
  });

  // Field ownership per AKT form instance. aktMainForm and aktExtraForm are
  // separate antd forms, but loading a page seeds each one with the full
  // formValues object — so both end up "registering" the other's fields. When
  // merging back, a stale value from the non-owning form could otherwise clobber
  // the live edit (spread order). Picking only each form's own fields prevents
  // that cross-contamination.
  const AKT_MAIN_FIELDS = [
    "actDocDate", "noActDocNumber", "actDocNumber", "actDocCost",
    "actTransCost", "currencyType", "actExchange", "actExchangeAmountInAccount",
  ];
  const AKT_EXTRA_FIELDS = [
    "actSender", "actServiceProvider", "actServiceProviderName",
    "goodServiceInfo", "countryId", "note",
  ];

  // Conversion outputs are not user input: the convert calls write them into the
  // page state only, never back into the antd store (unlike YGB, which keeps
  // `exchangeAmountInAccount` in its form). The store therefore still holds
  // whatever the page was seeded with when it was loaded, so letting it win the
  // merge reverted the converted amount of every page that was navigated away
  // from — which is why the exchange total differed from page to page. Merge the
  // user-owned fields only and keep the page as the source of truth for these.
  const AKT_DERIVED_FIELDS = ["actExchange", "actExchangeAmountInAccount"];
  const AKT_MAIN_INPUT_FIELDS = AKT_MAIN_FIELDS.filter((f) => !AKT_DERIVED_FIELDS.includes(f));

  const pickFields = (values: Record<string, any>, keys: string[]) => {
    const out: Record<string, any> = {};
    keys.forEach((k) => {
      if (values && k in values) out[k] = values[k];
    });
    return out;
  };

  const getCurrentAktMergedValues = () => ({
    ...(currentAktPage?.formValues || {}),
    ...pickFields(aktMainForm.getFieldsValue(true), AKT_MAIN_INPUT_FIELDS),
    ...pickFields(aktExtraForm.getFieldsValue(true), AKT_EXTRA_FIELDS),
  });

  const getCurrentAktMergedValuesWithDerived = () => ({
    ...(currentAktPage?.formValues || {}),
    ...pickFields(aktMainForm.getFieldsValue(true), AKT_MAIN_INPUT_FIELDS),
    ...pickFields(aktExtraForm.getFieldsValue(true), AKT_EXTRA_FIELDS),
  });

  const persistCurrentYgbPageForm = (override?: any) => {
    if (!currentYgbPage) return;
    setYgbPages((prev) => {
      const next = [...prev];
      const index = ygbPageIndex - 1;
      if (!next[index]) return prev;
      const prevValues = next[index].formValues || {};
      const incoming = override ?? {};
      next[index] = {
        ...next[index],
        formValues: {
          ...prevValues,
          ...incoming,
          exchange: incoming.exchange !== undefined ? incoming.exchange : prevValues.exchange,
          exchangeAmountInAccount:
            incoming.exchangeAmountInAccount !== undefined
              ? incoming.exchangeAmountInAccount
              : prevValues.exchangeAmountInAccount,
        },
      };
      return next;
    });
  };

  const persistCurrentAktPageForm = (override?: any) => {
    if (!currentAktPage) return;
    setAktPages((prev) => {
      const next = [...prev];
      const index = aktPageIndex - 1;
      if (!next[index]) return prev;
      const prevValues = next[index].formValues || {};
      const incoming = override ?? {};
      next[index] = {
        ...next[index],
        formValues: {
          ...prevValues,
          ...incoming,
          actExchange:
            incoming.actExchange !== undefined ? incoming.actExchange : prevValues.actExchange,
          actExchangeAmountInAccount:
            incoming.actExchangeAmountInAccount !== undefined
              ? incoming.actExchangeAmountInAccount
              : prevValues.actExchangeAmountInAccount,
        },
      };
      return next;
    });
  };

  const loadYgbPageToForms = (page?: YgbPage) => {
    suppressAutoConvertRef.current = true;
    ygbInfoForm.resetFields();
    ygbForm.resetFields();
    if (page) {
      ygbInfoForm.setFieldsValue(page.formValues || {});
      ygbForm.setFieldsValue(page.formValues || {});
    }
    setTimeout(() => { suppressAutoConvertRef.current = false; }, 0);
  };

  const loadAktPageToForms = (page?: AktPage) => {
    suppressAutoConvertRef.current = true;
    aktMainForm.resetFields();
    aktExtraForm.resetFields();
    if (page) {
      aktMainForm.setFieldsValue(pickFields(page.formValues || {}, AKT_MAIN_FIELDS));
      aktExtraForm.setFieldsValue(pickFields(page.formValues || {}, AKT_EXTRA_FIELDS));
    }
    // Keep a stored pick while it is still selectable; otherwise fall back to
    // the account currency so the single-option (disabled) case stays filled.
    if (toConvert && !isAllowedCurrency(aktCurrencyOptions, aktMainForm.getFieldValue("currencyType"))) {
      aktMainForm.setFieldValue("currencyType", toConvert);
    }
    setTimeout(() => { suppressAutoConvertRef.current = false; }, 0);
  };
  // Seeds the AKT currency and repairs a value the dropdown no longer offers
  // (a draft saved under a different account, or the transfer currency being
  // switched away from). A currency that is still selectable is left alone.
  useEffect(() => {
    if (!props.visible || props.transferKind !== "AKT" || !toConvert) return;
    if (isAllowedCurrency(aktCurrencyOptions, aktMainForm.getFieldValue("currencyType"))) return;
    aktMainForm.setFieldValue("currencyType", toConvert);
    persistCurrentAktPageForm({ currencyType: toConvert });
    applyAktConvertForValues(
      {
        ...(currentAktPage?.formValues || {}),
        currencyType: toConvert,
        actTransCost: aktMainForm.getFieldValue("actTransCost"),
      },
      aktPageIndex - 1,
      currentAktPage?.key
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind, toConvert, aktCurrencyOptions, aktPageIndex, currentAktPage?.key]);
  // Same rule for every Avans contract row: seed the account currency, repair a
  // value the dropdown no longer offers, and leave a valid pick untouched.
  useEffect(() => {
    if (!props.visible || props.transferKind !== "Avans" || !toConvert) return;
    const values = avansForm.getFieldsValue(true);
    for (let idx = 0; idx < (avansContracts?.length || 0); idx++) {
      const field = `avansCurrencyType-${idx}`;
      if (isAllowedCurrency(avansCurrencyOptions, values[field])) continue;
      avansForm.setFieldValue(field, toConvert);
      applyAvansConvertForValues({ ...values, [field]: toConvert }, idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind, toConvert, avansCurrencyOptions, avansContracts?.length]);

  const avansCurrencySelection = avansContracts
    .map((_: any, idx: number) => String(avansWatchedValues?.[`avansCurrencyType-${idx}`] ?? ""))
    .join("|");

  useEffect(() => {
    if (!props.visible || props.transferKind !== "Avans" || !openAvansRest) return;
    if (!avansContracts?.length) return;

    const values = avansForm.getFieldsValue(true);
    const patch: Record<string, string> = {};
    avansContracts.forEach((contract: any, idx: number) => {
      const next = avansAccountNoForDisplay(
        values[`avansCurrencyType-${idx}`],
        contract?.accountNo
      );
      if (String(values[`accountNo-${idx}`] ?? "") !== next) {
        patch[`accountNo-${idx}`] = next;
      }
    });
    if (!Object.keys(patch).length) return;
    avansForm.setFieldsValue(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind, props.iban, openAvansRest, avansContracts, avansCurrencySelection, toConvert]);

  const convertSingle = async (
    currency: string,
    amount: any
  ): Promise<{ formatted: string; amountInAccount: number } | null> => {
    const amt = normalizeDecimal(amount);
    const cur = String(currency ?? "").trim();
    if (!toConvert || !transferAmount || !cur || !Number.isFinite(amt) || amt <= 0) return null;
    if (cur === toConvert) return { formatted: "", amountInAccount: amt };
    const req: ConvertData = {
      toConvert,
      transferAmount,
      amountCurrency: [{ currency: cur, amount: amt }],
    };
    const res = await ConvertBulk(req);
    if (!res || res?.status !== 200 || !res?.data) {
      const msg = res?.messages?.[0]?.message || t("dgk.error_occurred");
      throw new Error(msg);
    }
    const convertedAmount = res.data.amount;
    const resultCurrency = res.data.currency;
    if (convertedAmount === undefined || convertedAmount === null || !resultCurrency) {
      throw new Error(t("dgk.error_occurred"));
    }
    const formatted = `${amt} ${cur} = ${convertedAmount} ${resultCurrency}`;
    return { formatted, amountInAccount: Number(convertedAmount) };
  };

  const applyYgbConvertForValues = async (
    values: any,
    pageIndex?: number,
    forcedPageKey?: string
  ) => {
    const cur = String(values?.currencyType ?? "").trim();
    const amt = values?.transferredAmount;
    const pageKey =
      forcedPageKey ??
      (typeof pageIndex === "number" ? ygbPages[pageIndex]?.key : currentYgbPage?.key);

    if (!cur || amt === undefined || amt === null || amt === "") {
      ygbForm.setFieldValue("exchange", "");
      ygbForm.setFieldValue("exchangeAmountInAccount", null);
      ygbForm.setFields([{ name: "exchange", errors: [] }]);
      if (pageKey) {
        const nextStatus = { ...ygbConvertStatus, [pageKey]: "idle" as const };
        setYgbConvertStatus(nextStatus);
        if (typeof pageIndex === "number") {
          setYgbPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, exchange: "", exchangeAmountInAccount: null },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ ygbConvertStatus: nextStatus });
      }
      return;
    }

    if (isSameCurrency(cur, toConvert)) {
      const amountInAccount = normalizeDecimal(amt);
      ygbForm.setFieldValue("exchange", "");
      ygbForm.setFieldValue("exchangeAmountInAccount", amountInAccount);
      ygbForm.setFields([{ name: "exchange", errors: [] }]);
      if (pageKey) {
        const nextStatus = { ...ygbConvertStatus, [pageKey]: "success" as const };
        setYgbConvertStatus(nextStatus);
        if (typeof pageIndex === "number") {
          setYgbPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, exchange: "", exchangeAmountInAccount: amountInAccount },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ ygbConvertStatus: nextStatus });
      }
      return;
    }

    ygbForm.setFieldValue("exchange", "");
    ygbForm.setFieldValue("exchangeAmountInAccount", null);
    ygbForm.setFields([{ name: "exchange", errors: [] }]);

    let loadingStatus = ygbConvertStatus;
    if (pageKey) {
      loadingStatus = { ...ygbConvertStatus, [pageKey]: "loading" as const };
      setYgbConvertStatus(loadingStatus);
      recomputeSaveEnabled({ ygbConvertStatus: loadingStatus });
    }

    try {
      const result = await convertSingle(cur, amt);
      const formatted = result?.formatted || "";
      const amountInAccount = result?.amountInAccount ?? null;
      ygbForm.setFieldValue("exchange", formatted);
      ygbForm.setFieldValue("exchangeAmountInAccount", amountInAccount);
      ygbForm.setFields([{ name: "exchange", errors: [] }]);
      if (pageKey) {
        const successStatus = { ...loadingStatus, [pageKey]: "success" as const };
        setYgbConvertStatus(successStatus);
        if (typeof pageIndex === "number") {
          setYgbPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, exchange: formatted, exchangeAmountInAccount: amountInAccount },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ ygbConvertStatus: successStatus });
      }
    } catch (e: any) {
      ygbForm.setFieldValue("exchange", "");
      ygbForm.setFieldValue("exchangeAmountInAccount", null);
      if (pageKey) {
        const errorStatus = { ...loadingStatus, [pageKey]: "error" as const };
        setYgbConvertStatus(errorStatus);
        if (typeof pageIndex === "number") {
          setYgbPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, exchange: "", exchangeAmountInAccount: null },
            };
            return next;
          });
        }
        const convertErrorMessage = getConvertErrorMessage(e, t);
        ygbForm.setFields([{ name: "exchange", errors: [convertErrorMessage] }]);
        recomputeSaveEnabled({ ygbConvertStatus: errorStatus });
      }
    }
  };

  const applyAktConvertForValues = async (
    values: any,
    pageIndex?: number,
    forcedPageKey?: string
  ) => {
    const cur = String(values?.currencyType ?? "").trim();
    const amt = values?.actTransCost;
    const pageKey =
      forcedPageKey ??
      (typeof pageIndex === "number" ? aktPages[pageIndex]?.key : currentAktPage?.key);

    if (!cur || amt === undefined || amt === null || amt === "") {
      aktMainForm.setFieldValue("actExchange", "");
      if (pageKey) {
        const nextStatus = { ...aktConvertStatus, [pageKey]: "idle" as const };
        setAktConvertStatus(nextStatus);
        if (typeof pageIndex === "number") {
          setAktPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, actExchange: "", actExchangeAmountInAccount: null },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ aktConvertStatus: nextStatus });
      }
      return;
    }

    if (isSameCurrency(cur, toConvert)) {
      const amountInAccount = normalizeDecimal(amt);
      aktMainForm.setFieldValue("actExchange", "");
      aktMainForm.setFields([{ name: "actExchange", errors: [] }]);
      if (pageKey) {
        const nextStatus = { ...aktConvertStatus, [pageKey]: "success" as const };
        setAktConvertStatus(nextStatus);
        if (typeof pageIndex === "number") {
          setAktPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, actExchange: "", actExchangeAmountInAccount: amountInAccount },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ aktConvertStatus: nextStatus });
      }
      return;
    }

    aktMainForm.setFieldValue("actExchange", "");
    aktMainForm.setFields([{ name: "actExchange", errors: [] }]);

    let loadingStatus = aktConvertStatus;
    if (pageKey) {
      loadingStatus = { ...aktConvertStatus, [pageKey]: "loading" as const };
      setAktConvertStatus(loadingStatus);
      recomputeSaveEnabled({ aktConvertStatus: loadingStatus });
    }

    try {
      const result = await convertSingle(cur, amt);
      const formatted = result?.formatted || "";
      const amountInAccount = result?.amountInAccount ?? null;
      aktMainForm.setFieldValue("actExchange", formatted);
      aktMainForm.setFields([{ name: "actExchange", errors: [] }]);
      if (pageKey) {
        const successStatus = { ...loadingStatus, [pageKey]: "success" as const };
        setAktConvertStatus(successStatus);
        if (typeof pageIndex === "number") {
          setAktPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, actExchange: formatted, actExchangeAmountInAccount: amountInAccount },
            };
            return next;
          });
        }
        recomputeSaveEnabled({ aktConvertStatus: successStatus });
      }
    } catch (e: any) {
      aktMainForm.setFieldValue("actExchange", "");
      if (pageKey) {
        const errorStatus = { ...loadingStatus, [pageKey]: "error" as const };
        setAktConvertStatus(errorStatus);
        if (typeof pageIndex === "number") {
          setAktPages((prev) => {
            const next = [...prev];
            if (!next[pageIndex]) return prev;
            next[pageIndex] = {
              ...next[pageIndex],
              formValues: { ...next[pageIndex].formValues, actExchange: "", actExchangeAmountInAccount: null },
            };
            return next;
          });
        }
        const convertErrorMessage = getConvertErrorMessage(e, t);
        aktMainForm.setFields([{ name: "actExchange", errors: [convertErrorMessage] }]);
        recomputeSaveEnabled({ aktConvertStatus: errorStatus });
      }
    }
  };

  const getAktTransferSum = (pages: AktPage[], currentValues?: any) => {
    const mergedPages = pages.map((p, idx) =>
      idx === aktPageIndex - 1
        ? { ...p, formValues: currentValues ?? getCurrentAktMergedValues() }
        : p
    );
    return mergedPages.reduce((acc, p) => {
      const a = normalizeDecimal(p.formValues?.actTransCost);
      return Number.isFinite(a) ? acc + a : acc;
    }, 0);
  };

  const applyAvansConvertForValues = async (values: any, index: number) => {
    const cur = String(values?.[`avansCurrencyType-${index}`] ?? "").trim();
    const amt = values?.[`avansTransferredAmount-${index}`];

    if (!cur || amt === undefined || amt === null || amt === "") {
      avansForm.setFieldValue(`avansExchange-${index}`, "");
      avansForm.setFieldValue(`avansExchangeAmountInAccount-${index}`, null);
      setAvansConvertStatus((prev) => ({ ...prev, [index]: "idle" }));
      return;
    }

    if (isSameCurrency(cur, toConvert)) {
      avansForm.setFieldValue(`avansExchange-${index}`, "");
      avansForm.setFieldValue(`avansExchangeAmountInAccount-${index}`, normalizeDecimal(amt));
      setAvansConvertStatus((prev) => ({ ...prev, [index]: "success" }));
      return;
    }

    try {
      const result = await convertSingle(cur, amt);
      const formatted = result?.formatted || "";
      const amountInAccount = result?.amountInAccount ?? null;
      avansForm.setFieldValue(`avansExchange-${index}`, formatted);
      avansForm.setFieldValue(`avansExchangeAmountInAccount-${index}`, amountInAccount);
      setAvansConvertStatus((prev) => ({ ...prev, [index]: "success" }));
      recomputeSaveEnabled();
    } catch (e) {
      avansForm.setFieldValue(`avansExchange-${index}`, "");
      avansForm.setFieldValue(`avansExchangeAmountInAccount-${index}`, null);
      setAvansConvertStatus((prev) => ({ ...prev, [index]: "error" }));
      console.error("Avans restore convert failed:", e);
    }
  };

  useEffect(() => {
    const hydrateYgbFromStore = async () => {
      if (!props.visible) return;
      if (props.transferKind !== "YGB") return;
      if (!dgkData || dgkData.type !== "YGB") return;
      setYgbConvertStatus({});
      const { countries } = await ensureLookupsLoaded();
      const pages = dgkData.pages || [];
      if (!pages.length) return;
      const resolveCountryId = (raw: any): string | undefined => {
        if (raw == null) return undefined;
        const str = String(raw);
        if (countries[str]) return str;
        return findCountryIdByName(countries, str);
      };
      const mappedPages = pages.map((p: any) => ({
        key: p.gbNo,
        gbNo: p.gbNo,
        serverData: {
          gbNo: p.gbNo,
          name: p.summary?.name,
          taxId: p.summary?.taxId,
          receiverCountry: p.summary?.receiverCountry,
          invoiceCost: p.summary?.invoiceCost,
          operationAmount: p.summary?.operationAmount,
          operationCurrency: p.summary?.operationCurrency,
          egbCurrency: p.summary?.egbCurrency,
          egbSum: p.summary?.egbSum,
          invoiceCurrency: p.summary?.invoiceCurrency,
          deliveryCondition: p.summary?.deliveryCondition,
          bargainingFeature: p.summary?.bargainingFeature,
          impDocDate: p.summary?.impDocDate,
          impDocCost: p.summary?.impDocCost,
          declarationTypeKey: p.summary?.declarationTypeKey,
          senderOrExporter: p.summary?.senderOrExporter,
          goodOfServiceInfo: p.summary?.goodOfServiceInfo,
          note: p.summary?.note,
          bankUserNumber: p.summary?.bankUserNumber,
          pincode: p.summary?.pincode ?? p.summary?.pinCode,
          contracts: p.contracts || [],
          details: p.details || [],
          filePath: p.filePath || null,
        },
        docNoOptions: (p.contracts || []).map((c: any) => ({ label: c.no, value: c.no })),
        formValues: {
          ...(p.form || {}),
          receiverCountryId: resolveCountryId(p.form?.receiverCountryId),
          egbSum: (() => {
            const raw = p.summary?.egbSum ?? p.form?.egbSum;
            if (raw == null) return "";
            const num = Number(raw);
            if (!Number.isFinite(num)) return String(raw || "");
            return `${num.toFixed(2)} ${p.summary?.egbCurrency || ""}`.trim();
          })(),
          exchange: p.form?.exchange || "",
          exchangeAmountInAccount: p.form?.exchangeAmountInAccount ?? null,
        },
      }));
      suppressAutoConvertRef.current = true;
      setYgbPages(mappedPages);
      setYgbPageIndex(1);
      setOpenYgbRest(true);
      setYgbPaymentsPage(1);
      setTimeout(async () => {
        loadYgbPageToForms(mappedPages[0]);
        suppressAutoConvertRef.current = false;
        await applyYgbConvertForValues(mappedPages[0]?.formValues, 0, mappedPages[0]?.key);
      }, 0);
    };
    hydrateYgbFromStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind, dgkData]);

  useEffect(() => {
    const hydrateAktFromStore = async () => {
      if (!props.visible) return;
      if (props.transferKind !== "AKT") return;
      if (!dgkData || dgkData.type !== "AKT") {
  const fresh: AktPage = {
    key: `akt-${Date.now()}`,
    formValues: {
      actExchange: "",
      actExchangeAmountInAccount: null,
    },
  };

  suppressAutoConvertRef.current = true;

  setAktPages([fresh]);
  setAktPageIndex(1);

  aktMainForm.resetFields();
  aktExtraForm.resetFields();

  setTimeout(() => {
    loadAktPageToForms(fresh);
    suppressAutoConvertRef.current = false;
  }, 0);

  return;
}
      setAktConvertStatus({});
      await ensureLookupsLoaded();
      const pages = dgkData.pages || [];
      if (!pages.length) return;
      const mappedPages = pages.map((p: any, idx: number) => ({
        key: p.key || `akt-${idx}`,
        formValues: {
          ...(p.form || {}),
          actDocDate: p.form?.actDocDate ? dayjs(p.form.actDocDate) : null,
          actExchange: p.form?.actExchange || "",
          actExchangeAmountInAccount: p.form?.actExchangeAmountInAccount ?? null,
        },
      }));
      suppressAutoConvertRef.current = true;
      setAktPages(mappedPages);
      setAktPageIndex(1);
      setTimeout(async () => {
        loadAktPageToForms(mappedPages[0]);
        suppressAutoConvertRef.current = false;
        await applyAktConvertForValues(mappedPages[0]?.formValues, 0, mappedPages[0]?.key);
      }, 0);
    };
    hydrateAktFromStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind, dgkData]);

  useEffect(() => {
  let cancelled = false;

  const hydrateAvansFromStore = async () => {
    if (!props.visible) return;
    if (props.transferKind !== "Avans") return;

    if (!dgkData || dgkData.type !== "Avans") {
      setOpenAvansRest(false);
      return;
    }

    setAvansConvertStatus({});
    await ensureLookupsLoaded();
    if (cancelled) return;

    setAvansData({
      pinCode: dgkData?.pinCode,
      taxId: dgkData?.summary?.taxId,
      name: dgkData?.summary?.name,
      details: dgkData?.details || [],
      gbList: dgkData?.gbList || [],
      docFile: dgkData?.docFile,
    });

    setAvansContracts(dgkData?.contracts || []);
    setOpenAvansRest(true);
    setAvansPage(1);

    suppressAutoConvertRef.current = true;
    const hydratedForm = { ...(dgkData?.form || {}) };
    (dgkData?.contracts || []).forEach((contract: any, idx: number) => {
      hydratedForm[`accountNo-${idx}`] = avansAccountNoForDisplay(
        hydratedForm[`avansCurrencyType-${idx}`],
        contract?.accountNo
      );
    });
    avansForm.resetFields();
    avansForm.setFieldsValue(hydratedForm);

    if (
  toConvert !== "RUB" &&
  !avansForm.getFieldValue(`avansReceiver-${0}`) &&
  props?.beneficiaryName
) {
  avansForm.setFieldValue(
    `avansReceiver-${0}`,
    props.beneficiaryName
  );
}

    setTimeout(async () => {
      if (cancelled) return;

      suppressAutoConvertRef.current = false;

      const firstIndex = 0;
      const values = dgkData?.form || {};

      if (
        values[`avansCurrencyType-${firstIndex}`] &&
        values[`avansTransferredAmount-${firstIndex}`]
      ) {
        await applyAvansConvertForValues(values, firstIndex);
      }
    }, 0);
  };

  hydrateAvansFromStore();

  return () => {
    cancelled = true;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [props.visible, props.transferKind, dgkData]);

  const handleYgbConvert = () => {
    if (suppressAutoConvertRef.current) return;
    if (!props.visible || props.transferKind !== "YGB") return;
    if (!currentYgbPage) return;

    const values  = ygbForm.getFieldsValue(true);
    const cur     = String(values.currencyType ?? "").trim();
    const amt     = values.transferredAmount;
    const pageKey = currentYgbPage.key;
    const sameCur = isSameCurrency(cur, toConvert);

    clearTimeout(ygbConvertTimer.current);

    if (!cur || amt === undefined || amt === null || amt === "") {
      const nextFormValues = { ...getCurrentYgbMergedValuesWithDerived(), exchange: "", exchangeAmountInAccount: null };
      const nextPages  = buildNextYgbPages(nextFormValues);
      const nextStatus = { ...ygbConvertStatus, [pageKey]: "idle" as const };
      ygbForm.setFieldValue("exchange", "");
      ygbForm.setFieldValue("exchangeAmountInAccount", null);
      ygbForm.setFields([{ name: "exchange", errors: [] }]);
      setYgbConvertStatus(nextStatus);
      persistCurrentYgbPageForm(nextFormValues);
      recomputeSaveEnabled({ ygbPages: nextPages, ygbConvertStatus: nextStatus });
      return;
    }

    if (sameCur) {
      const amountInAccount = normalizeDecimal(amt);
      const nextFormValues  = { ...getCurrentYgbMergedValuesWithDerived(), exchange: "", exchangeAmountInAccount: amountInAccount };
      const nextPages  = buildNextYgbPages(nextFormValues);
      const cross      = validateYgbCrossRules(nextPages);

      ygbForm.setFieldValue("exchange", "");
      ygbForm.setFieldValue("exchangeAmountInAccount", amountInAccount);
      ygbForm.setFields([{ name: "exchange", errors: [] }]);

      if (cross.sumExceedsTransfer) {
        ygbForm.setFields([{ name: "transferredAmount", errors: [t("dgk.gb_transfer_exceed")] }]);
        const errorStatus = { ...ygbConvertStatus, [pageKey]: "error" as const };
        setYgbConvertStatus(errorStatus);
        persistCurrentYgbPageForm({ ...nextFormValues, exchangeAmountInAccount: null });
        recomputeSaveEnabled({
          ygbPages: buildNextYgbPages({ ...nextFormValues, exchangeAmountInAccount: null }),
          ygbConvertStatus: errorStatus,
        });
        return;
      }

      const nextStatus = { ...ygbConvertStatus, [pageKey]: "success" as const };
      setYgbConvertStatus(nextStatus);
      persistCurrentYgbPageForm(nextFormValues);
      recomputeSaveEnabled({ ygbPages: nextPages, ygbConvertStatus: nextStatus });
      return;
    }

    ygbForm.setFieldValue("exchange", "");
    ygbForm.setFieldValue("exchangeAmountInAccount", null);
    ygbForm.setFields([{ name: "exchange", errors: [] }]);

    const loadingFormValues = { ...getCurrentYgbMergedValuesWithDerived(), exchange: "", exchangeAmountInAccount: null };
    const loadingPages  = buildNextYgbPages(loadingFormValues);
    const loadingStatus = { ...ygbConvertStatus, [pageKey]: "loading" as const };
    setYgbConvertStatus(loadingStatus);
    persistCurrentYgbPageForm(loadingFormValues);
    recomputeSaveEnabled({ ygbPages: loadingPages, ygbConvertStatus: loadingStatus });

    ygbConvertTimer.current = setTimeout(async () => {
      const reqId = ++ygbConvertRequestId.current;
      try {
        const result = await convertSingle(cur, amt);
        if (reqId !== ygbConvertRequestId.current) return;

        const formatted      = result?.formatted || "";
        const amountInAccount = result?.amountInAccount ?? null;
        ygbForm.setFieldValue("exchange", formatted);
        ygbForm.setFieldValue("exchangeAmountInAccount", amountInAccount);
        ygbForm.setFields([{ name: "exchange", errors: [] }]);

        const successFormValues = { ...getCurrentYgbMergedValuesWithDerived(), exchange: formatted, exchangeAmountInAccount: amountInAccount };
        const successPages  = buildNextYgbPages(successFormValues);
        const successStatus = { ...loadingStatus, [pageKey]: "success" as const };
        setYgbConvertStatus(successStatus);
        persistCurrentYgbPageForm(successFormValues);
        recomputeSaveEnabled({ ygbPages: successPages, ygbConvertStatus: successStatus });
      } catch (e: any) {
        if (reqId !== ygbConvertRequestId.current) return;

        const convertErrorMessage = getConvertErrorMessage(e, t);

        const isTransferExceedError =
          String(e?.message || "").includes("CURRENCY_CONVERT_ERROR") ||
          String(e?.message || "").includes("Konvertasiya olunan məbləğ transfer məbləğindən böyükdür");

        ygbForm.setFields([
          {
            name: "transferredAmount",
            errors: isTransferExceedError ? [t("dgk.gb_transfer_exceed")] : [],
          },
          {
            name: "exchange",
            errors: [convertErrorMessage],
          },
        ]);

        const errorFormValues = { ...getCurrentYgbMergedValuesWithDerived(), exchange: "", exchangeAmountInAccount: null };
        const errorPages  = buildNextYgbPages(errorFormValues);
        const errorStatus = { ...loadingStatus, [pageKey]: "error" as const };
        setYgbConvertStatus(errorStatus);
        persistCurrentYgbPageForm(errorFormValues);
        recomputeSaveEnabled({ ygbPages: errorPages, ygbConvertStatus: errorStatus });
      }
    }, 450);
  };

  const handleAktConvert = () => {
    if (suppressAutoConvertRef.current) return;
    if (!props.visible || props.transferKind !== "AKT") return;
    if (!currentAktPage) return;

    const values  = aktMainForm.getFieldsValue(true);
    const cur     = String(values.currencyType ?? "").trim();
    const amt     = values.actTransCost;
    const pageKey = currentAktPage.key;
    const sameCur = isSameCurrency(cur, toConvert);

    clearTimeout(aktConvertTimer.current);

    if (!cur || amt === undefined || amt === null || amt === "") {
      const nextFormValues = { ...getCurrentAktMergedValues(), actExchange: "", actExchangeAmountInAccount: null };
      const nextPages  = buildNextAktPages(nextFormValues);
      const nextStatus = { ...aktConvertStatus, [pageKey]: "idle" as const };
      aktMainForm.setFieldValue("actExchange", "");
      aktMainForm.setFields([{ name: "actExchange", errors: [] }]);
      setAktConvertStatus(nextStatus);
      persistCurrentAktPageForm(nextFormValues);
      recomputeSaveEnabled({ aktPages: nextPages, aktConvertStatus: nextStatus });
      return;
    }

    if (sameCur) {
      const amountInAccount = normalizeDecimal(amt);
      const nextFormValues  = { ...getCurrentAktMergedValues(), actExchange: "", actExchangeAmountInAccount: amountInAccount };
      const nextPages  = buildNextAktPages(nextFormValues);
      const cross      = validateAktTransferSum(nextPages);

      aktMainForm.setFieldValue("actExchange", "");
      aktMainForm.setFields([{ name: "actExchange", errors: [] }]);

      if (cross.sumExceedsTransfer) {
        aktMainForm.setFields([{ name: "actTransCost", errors: [t("dgk.akt_transfer_exceed")] }]);
        const errorStatus = { ...aktConvertStatus, [pageKey]: "error" as const };
        setAktConvertStatus(errorStatus);
        persistCurrentAktPageForm({ ...nextFormValues, actExchangeAmountInAccount: null });
        recomputeSaveEnabled({
          aktPages: buildNextAktPages({ ...nextFormValues, actExchangeAmountInAccount: null }),
          aktConvertStatus: errorStatus,
        });
        return;
      }

      const nextStatus = { ...aktConvertStatus, [pageKey]: "success" as const };
      setAktConvertStatus(nextStatus);
      persistCurrentAktPageForm(nextFormValues);
      recomputeSaveEnabled({ aktPages: nextPages, aktConvertStatus: nextStatus });
      return;
    }

    aktMainForm.setFieldValue("actExchange", "");
    aktMainForm.setFields([{ name: "actExchange", errors: [] }]);

    const loadingFormValues = { ...getCurrentAktMergedValues(), actExchange: "", actExchangeAmountInAccount: null };
    const loadingPages  = buildNextAktPages(loadingFormValues);
    const loadingStatus = { ...aktConvertStatus, [pageKey]: "loading" as const };
    setAktConvertStatus(loadingStatus);
    persistCurrentAktPageForm(loadingFormValues);
    recomputeSaveEnabled({ aktPages: loadingPages, aktConvertStatus: loadingStatus });

    aktConvertTimer.current = setTimeout(async () => {
      const reqId = ++aktConvertRequestId.current;
      try {
        const result = await convertSingle(cur, amt);
        if (reqId !== aktConvertRequestId.current) return;

        const formatted      = result?.formatted || "";
        const amountInAccount = result?.amountInAccount ?? null;
        aktMainForm.setFieldValue("actExchange", formatted);
        aktMainForm.setFields([{ name: "actExchange", errors: [] }]);

        const successFormValues = { ...getCurrentAktMergedValues(), actExchange: formatted, actExchangeAmountInAccount: amountInAccount };
        const successPages  = buildNextAktPages(successFormValues);

        const exchangeTotal = computeExchangeTotalFromPages(successPages as any, "AKT");
        if (Number.isFinite(amountInAccount) && Number.isFinite(transferAmount) && exchangeTotal > transferAmount) {
          aktMainForm.setFields([{ name: "actTransCost", errors: [t("dgk.akt_transfer_exceed")] }]);
          const errorStatus = { ...loadingStatus, [pageKey]: "error" as const };
          setAktConvertStatus(errorStatus);
          persistCurrentAktPageForm({ ...successFormValues, actExchangeAmountInAccount: null });
          recomputeSaveEnabled({
            aktPages: buildNextAktPages({ ...successFormValues, actExchangeAmountInAccount: null }),
            aktConvertStatus: errorStatus,
          });
          return;
        }

        const successStatus = { ...loadingStatus, [pageKey]: "success" as const };
        setAktConvertStatus(successStatus);
        persistCurrentAktPageForm(successFormValues);
        recomputeSaveEnabled({ aktPages: successPages, aktConvertStatus: successStatus });
      } catch (e: any) {
        if (reqId !== aktConvertRequestId.current) return;

        const convertErrorMessage = getConvertErrorMessage(e, t);

        const isTransferExceedError =
          String(e?.message || "").includes("CURRENCY_CONVERT_ERROR") ||
          String(e?.message || "").includes("Konvertasiya olunan məbləğ transfer məbləğindən böyükdür");

        aktMainForm.setFields([
          {
            name: "actTransCost",
            errors: isTransferExceedError ? [t("dgk.akt_transfer_exceed")] : [],
          },
          {
            name: "actExchange",
            errors: [convertErrorMessage],
          },
        ]);

        const errorFormValues = { ...getCurrentAktMergedValues(), actExchange: "", actExchangeAmountInAccount: null };
        const errorPages  = buildNextAktPages(errorFormValues);
        const errorStatus = { ...loadingStatus, [pageKey]: "error" as const };
        setAktConvertStatus(errorStatus);
        persistCurrentAktPageForm(errorFormValues);
        recomputeSaveEnabled({ aktPages: errorPages, aktConvertStatus: errorStatus });
      }
    }, 450);
  };

  const validateAktTransferSum = (pages: AktPage[]) => {
    const sum = pages.reduce((acc, p) => {
      const a = normalizeDecimal(p.formValues?.actTransCost);
      return Number.isFinite(a) ? acc + a : acc;
    }, 0);
    const sumExceedsTransfer =
      Number.isFinite(sum) && Number.isFinite(transferAmount) && sum > transferAmount;
    return { sum, sumExceedsTransfer };
  };

  const getConvertErrorMessage = (error: any, t: any) => {
    const rawMessage = String(error?.message || "").trim();
    if (
      rawMessage.includes("CURRENCY_CONVERT_ERROR") ||
      rawMessage.includes("Konvertasiya olunan məbləğ transfer məbləğindən böyükdür")
    ) {
      return t("dgk.error_occurred");
    }
    return rawMessage || t("dgk.error_occurred");
  };

  const handleAvansConvert = () => {
    if (suppressAutoConvertRef.current) return;
    if (!props.visible || props.transferKind !== "Avans") return;
    if (!openAvansRest) return;

    const i             = avansStart;
    const exchangeField = `avansExchange-${i}`;
    const amountField   = `avansTransferredAmount-${i}`;
    const values        = avansForm.getFieldsValue(true);
    const cur           = String(values[`avansCurrencyType-${i}`] ?? "").trim();
    const amt           = values[amountField];
    const sameCur       = isSameCurrency(cur, toConvert);

    clearTimeout(avansConvertTimer.current);

    if (!cur || amt === undefined || amt === null || amt === "") {
      preserveModalScroll(exchangeField, () => {
        avansForm.setFieldValue(exchangeField, "");
        avansForm.setFieldValue(`avansExchangeAmountInAccount-${i}`, null);
        avansForm.setFields([{ name: exchangeField, errors: [] }]);
      });
      setAvansConvertStatus((prev) => ({ ...prev, [i]: "idle" }));
      recomputeSaveEnabled();
      return;
    }

    if (sameCur) {
      const amountInAccount = normalizeDecimal(amt);
      let sum = 0;
      for (let idx = 0; idx < (avansContracts?.length || 0); idx++) {
        const v = idx === i ? amt : values[`avansTransferredAmount-${idx}`];
        const a = normalizeDecimal(v);
        if (Number.isFinite(a)) sum += a;
      }
      if (Number.isFinite(transferAmount) && sum > transferAmount) {
        preserveModalScroll(exchangeField, () => {
          avansForm.setFieldValue(exchangeField, "");
          avansForm.setFieldValue(`avansExchangeAmountInAccount-${i}`, null);
          avansForm.setFields([{ name: amountField, errors: [t("dgk.advance_transfer_exceed")] }]);
        });
        setAvansConvertStatus((prev) => ({ ...prev, [i]: "error" }));
        recomputeSaveEnabled();
        return;
      }
      preserveModalScroll(exchangeField, () => {
        avansForm.setFieldValue(exchangeField, "");
        avansForm.setFieldValue(`avansExchangeAmountInAccount-${i}`, amountInAccount);
        avansForm.setFields([{ name: exchangeField, errors: [] }]);
      });
      setAvansConvertStatus((prev) => ({ ...prev, [i]: "success" }));
      recomputeSaveEnabled();
      return;
    }

    preserveModalScroll(exchangeField, () => {
      avansForm.setFieldValue(exchangeField, "");
      avansForm.setFieldValue(`avansExchangeAmountInAccount-${i}`, null);
      avansForm.setFields([{ name: exchangeField, errors: [] }]);
    });
    setAvansConvertStatus((prev) => ({ ...prev, [i]: "loading" }));
    recomputeSaveEnabled();

    avansConvertTimer.current = setTimeout(async () => {
      const reqId = ++avansConvertRequestId.current;
      try {
        const result = await convertSingle(cur, amt);
        if (reqId !== avansConvertRequestId.current) return;

        preserveModalScroll(exchangeField, () => {
          avansForm.setFieldValue(exchangeField, result?.formatted || "");
          avansForm.setFieldValue(`avansExchangeAmountInAccount-${i}`, result?.amountInAccount ?? null);
          avansForm.setFields([{ name: exchangeField, errors: [] }]);
        });
        setAvansConvertStatus((prev) => ({ ...prev, [i]: "success" }));
        recomputeSaveEnabled();
      } catch (e: any) {
        if (reqId !== avansConvertRequestId.current) return;

        const convertErrorMessage = getConvertErrorMessage(e, t);

        const isTransferExceedError =
          String(e?.message || "").includes("CURRENCY_CONVERT_ERROR") ||
          String(e?.message || "").includes("Konvertasiya olunan məbləğ transfer məbləğindən böyükdür");

        preserveModalScroll(exchangeField, () => {
          avansForm.setFields([
            {
              name: `avansTransferredAmount-${i}`,
              errors: isTransferExceedError ? [t("dgk.advance_transfer_exceed")] : [],
            },
            {
              name: exchangeField,
              errors: [convertErrorMessage],
            },
          ]);
        });
        setAvansConvertStatus((prev) => ({ ...prev, [i]: "error" }));
        recomputeSaveEnabled();
      }
    }, 450);
  };

  const getAllYgbTransferredSum = (pages: YgbPage[]) => {
    let sum = 0;
    for (const p of pages) {
      const a = normalizeDecimal(p.formValues?.transferredAmount);
      if (Number.isFinite(a)) sum += a;
    }
    return sum;
  };

  const validateYgbCrossRules = (pages: YgbPage[]) => {
    const errorsByGbNo: Record<string, string | null> = {};
    const sum = getAllYgbTransferredSum(pages);
    for (const p of pages) {
      const amt    = normalizeDecimal(p.formValues?.transferredAmount);
      const egbSum = normalizeDecimal(p.serverData?.egbSum);
      if (Number.isFinite(amt) && Number.isFinite(egbSum) && amt > egbSum) {
        errorsByGbNo[p.gbNo] = t("dgk.gb_amount_vs_remaining_error");
      } else {
        errorsByGbNo[p.gbNo] = null;
      }
    }
    const sumExceedsTransfer =
      Number.isFinite(sum) && Number.isFinite(transferAmount) && sum > transferAmount;
    return { errorsByGbNo, sum, sumExceedsTransfer };
  };

  const validateAktUniqueness = (pages: AktPage[]) => {
    const seen = new Set<string>();
    for (const p of pages) {
      const d = p.formValues?.actDocDate ? dayjs(p.formValues.actDocDate).format("YYYY-MM-DD") : "";
      const n = p.formValues?.noActDocNumber ? "NONE" : String(p.formValues?.actDocNumber ?? "").trim();
      if (!d) continue;
      const key = `${d}__${n}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  };

  const checkAktDuplicate = (currentDate: any, currentNoNumber: boolean, currentNumber: string) => {
    const d = currentDate ? dayjs(currentDate).format("YYYY-MM-DD") : "";
    const n = currentNoNumber ? "NONE" : String(currentNumber ?? "").trim();
    if (!d) return false;

    const key = `${d}__${n}`;

    return aktPages.some((p, idx) => {
      if (idx === aktPageIndex - 1) return false; 
      
      const pd = p.formValues?.actDocDate ? dayjs(p.formValues.actDocDate).format("YYYY-MM-DD") : "";
      const pn = p.formValues?.noActDocNumber ? "NONE" : String(p.formValues?.actDocNumber ?? "").trim();
      if (!pd) return false;
      
      return `${pd}__${pn}` === key;
    });
  };

  const recomputeSaveEnabled = async (override?: {
    ygbPages?: YgbPage[];
    aktPages?: AktPage[];
    ygbConvertStatus?: Record<string, "idle" | "loading" | "success" | "error">;
    aktConvertStatus?: Record<string, "idle" | "loading" | "success" | "error">;
    avansConvertStatus?: Record<number, "idle" | "loading" | "success" | "error">;
  }) => {
    try {
      const effectiveYgbPages         = override?.ygbPages ?? ygbPages;
      const effectiveAktPages         = override?.aktPages ?? aktPages;
      const effectiveYgbConvertStatus = override?.ygbConvertStatus ?? ygbConvertStatus;
      const effectiveAktConvertStatus = override?.aktConvertStatus ?? aktConvertStatus;
      const effectiveAvansConvertStatus = override?.avansConvertStatus ?? avansConvertStatus;

      if (!props.visible) return setSaveEnabled(false);

      if (props.transferKind === "YGB") {
        if (effectiveYgbPages.length < 1) return setSaveEnabled(false);

        for (const p of effectiveYgbPages) {
          const v      = p.formValues || {};
          const egbSum = normalizeDecimal(p.serverData?.egbSum);

          if (Number.isFinite(egbSum) && egbSum === 0) return setSaveEnabled(false);
          if (!v.contractNo) return setSaveEnabled(false);
          if (!v.receiverCountryId) return setSaveEnabled(false);
          if (!v.receiverName) return setSaveEnabled(false);
          if (!isEnglishText(v.receiverName) || String(v.receiverName).length > 300) return setSaveEnabled(false);
          if (!v.currencyType) return setSaveEnabled(false);
          if (!v.transferredAmount) return setSaveEnabled(false);
          if (!isDecimalString(v.transferredAmount)) return setSaveEnabled(false);
          if (String(v.transferredAmount).length > 30) return setSaveEnabled(false);

          const needsConvert   = v.currencyType && !isSameCurrency(v.currencyType, toConvert);
          const convertStatus  = effectiveYgbConvertStatus[p.key] || "idle";
          if (needsConvert && convertStatus !== "success") return setSaveEnabled(false);
        }

        if (effectiveYgbPages.every((p) => isSameCurrency(p.formValues?.currencyType, toConvert))) {
          const cross = validateYgbCrossRules(effectiveYgbPages);
          if (cross.sumExceedsTransfer) return setSaveEnabled(false);
          if (Object.values(cross.errorsByGbNo).some(Boolean)) return setSaveEnabled(false);
        }

        return setSaveEnabled(true);
      }

      if (props.transferKind === "AKT") {
        for (const p of effectiveAktPages) {
          const v = p.formValues || {};

          if (!v.actDocDate) return setSaveEnabled(false);
          const noNumber = !!v.noActDocNumber;
          if (!noNumber) {
            if (!v.actDocNumber) return setSaveEnabled(false);
            if (String(v.actDocNumber).length > 50) return setSaveEnabled(false);
            if (hasForbiddenSymbols(v.actDocNumber)) return setSaveEnabled(false);
          }
          if (!v.actDocCost) return setSaveEnabled(false);
          if (!isMoney9x2(v.actDocCost)) return setSaveEnabled(false);
          if (!v.actTransCost) return setSaveEnabled(false);
          if (!isMoney9x2(v.actTransCost)) return setSaveEnabled(false);
          if (!v.currencyType) return setSaveEnabled(false);

          // The transferred amount may not exceed the document's total amount.
          const pageDocCost   = normalizeDecimal(v.actDocCost);
          const pageTransCost = normalizeDecimal(v.actTransCost);
          if (
            Number.isFinite(pageDocCost) &&
            Number.isFinite(pageTransCost) &&
            pageTransCost > pageDocCost
          ) {
            return setSaveEnabled(false);
          }

          const providerExists = !!v.actServiceProvider;
          if (providerExists) {
            if (!v.actServiceProviderName) return setSaveEnabled(false);
            if (!isEnglishText(v.actServiceProviderName) || hasForbiddenSymbols(v.actServiceProviderName) || String(v.actServiceProviderName).length > 300) return setSaveEnabled(false);
          } else {
            if (!v.actSender) return setSaveEnabled(false);
            if (!isEnglishText(v.actSender) || hasForbiddenSymbols(v.actSender) || String(v.actSender).length > 300) return setSaveEnabled(false);
          }

          if (!v.goodServiceInfo) return setSaveEnabled(false);
          if (!isEnglishText(v.goodServiceInfo) || hasForbiddenSymbols(v.goodServiceInfo) || String(v.goodServiceInfo).length > 300) return setSaveEnabled(false);
          if (!v.countryId) return setSaveEnabled(false);

          const needsConvert  = v.currencyType && !isSameCurrency(v.currencyType, toConvert);
          const convertStatus = effectiveAktConvertStatus[p.key] || "idle";
          if (needsConvert && convertStatus !== "success") return setSaveEnabled(false);
        }

        if (!validateAktUniqueness(effectiveAktPages)) return setSaveEnabled(false);

        const aktSum = computeExchangeTotalFromPages(effectiveAktPages as any, "AKT");
        if (Number.isFinite(transferAmount) && aktSum > transferAmount) return setSaveEnabled(false);

        return setSaveEnabled(true);
      }

      if (props.transferKind === "Avans") {
        if (!openAvansRest || !avansData) return setSaveEnabled(false);

        const values = avansForm.getFieldsValue(true);

        for (let idx = 0; idx < (avansContracts?.length || 0); idx++) {
          const rc  = values[`avansReceiverCountryId-${idx}`];
          const rn  = values[`avansReceiver-${idx}`];
          const amt = values[`avansTransferredAmount-${idx}`];
          const cur = values[`avansCurrencyType-${idx}`];

          if (!rc || !rn || !amt || !cur) return setSaveEnabled(false);
          if (!isEnglishText(rn) || hasForbiddenSymbols(rn) || String(rn).length > 300) return setSaveEnabled(false);
          if (!isDecimalString(amt) || String(amt).length > 30) return setSaveEnabled(false);

          const needsConvert  = cur && !isSameCurrency(cur, toConvert);
          const convertStatus = effectiveAvansConvertStatus[idx] || "idle";
          if (needsConvert && convertStatus !== "success") return setSaveEnabled(false);
        }

        const avansAllSame = Array.from(
          { length: avansContracts?.length || 0 },
          (_, idx) => isSameCurrency(values[`avansCurrencyType-${idx}`], toConvert)
        ).every(Boolean);

        if (avansAllSame) {
          let sum = 0;
          for (let idx = 0; idx < (avansContracts?.length || 0); idx++) {
            const a = normalizeDecimal(values[`avansTransferredAmount-${idx}`]);
            if (Number.isFinite(a)) sum += a;
          }
          if (Number.isFinite(transferAmount) && sum > transferAmount) return setSaveEnabled(false);
        }

        return setSaveEnabled(true);
      }

      setSaveEnabled(false);
    } catch {
      setSaveEnabled(false);
    }
  };

  useEffect(() => {
    recomputeSaveEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.visible,
    props.transferKind,
    ygbPages.length,
    ygbPageIndex,
    aktPages.length,
    aktPageIndex,
    openAvansRest,
    avansContracts.length,
    avansPage,
    ygbConvertStatus,
    aktConvertStatus,
    avansConvertStatus,
  ]);
  
  useEffect(() => {
    if (!props.visible) {
      setDgkErrorMessage(null);
      return;
    }
    if (props.transferKind === "AKT") ensureLookupsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.transferKind]);

  const getYgbInfo = async () => {
    setSearchGbLoading(true);
    setDgkErrorMessage(null);
    const gbNo = String(getInfoForm.getFieldValue("validationNumber") || "").trim();

    if (!/^\d{1,14}$/.test(gbNo)) {
      getInfoForm.setFields([{ name: "validationNumber", errors: [t("dgk.enter_valid_declaration_number")] }]);
      setSearchGbLoading(false);
      return;
    }

    if (ygbPages.some((p) => p.gbNo === gbNo)) {
      getInfoForm.setFields([{ name: "validationNumber", errors: [t("dgk.declaration_exists")] }]);
      setSearchGbLoading(false);
      return;
    }

    const payload: YGB = { gbNo, iban: props?.iban };

    try {
      const res  = await GetInfByGBNO(payload);
      const data = res?.data;

      if (!data) {
        getInfoForm.setFields([{ name: "validationNumber", errors: [t("dgk.declaration_not_found")] }]);
        return;
      }

      const lookups          = await ensureLookupsLoaded();
      persistCurrentYgbPageForm(getCurrentYgbMergedValuesWithDerived());

      const newDocNoOptions    = (data?.contracts || []).map((c: any) => ({ label: c.no, value: c.no }));
      const receiverCountryId  = findCountryIdByName(lookups.countries, data?.receiverCountry);

      const defaults = {
        gbNo: data.gbNo,
        name: data.name,
        receiverCountry: data.receiverCountry,
        receiverName: toConvert !== "RUB" ? String(props?.beneficiaryName ?? "").trim() : "",
        invoiceCost: data.invoiceCost ? `${data.invoiceCost} ${data.invoiceCurrency || ""}`.trim() : "",
        operationAmount: data.operationAmount ? `${data.operationAmount} ${data.operationCurrency || ""}`.trim() : "",
        egbSum: (data.egbSum != null) ? `${Number(data.egbSum).toFixed(2)} ${data.egbCurrency || ""}`.trim() : "",
        contractDate: null,
        receiverCountryId,
        transferredAmount: undefined,
        currencyType: undefined,
        exchange: "",
        exchangeAmountInAccount: null,
      };

      const page: YgbPage = {
        key: data.gbNo,
        gbNo: data.gbNo,
        serverData: data,
        docNoOptions: newDocNoOptions,
        formValues: defaults,
      };

      setYgbConvertStatus((prev) => ({ ...prev, [page.key]: "idle" }));
      setYgbPages((prev) => {
        const next = [...prev, page];
        setTimeout(() => {
          setYgbPageIndex(next.length);
          loadYgbPageToForms(page);
          recomputeSaveEnabled({ ygbPages: next });
        }, 0);
        return next;
      });

      setOpenYgbRest(true);
      setYgbPaymentsPage(1);
      getInfoForm.setFieldsValue({ validationNumber: undefined });
      getInfoForm.setFields([{ name: "validationNumber", errors: [] }]);
      setDgkErrorMessage(null);
    } catch (error) {
      if (isDgkSearchUnavailableError(error)) {
        setDgkErrorMessage(pickDgkApiMessage(error));
        getInfoForm.setFields([{ name: "validationNumber", errors: [] }]);
      } else {
        getInfoForm.setFields([{ name: "validationNumber", errors: [t("dgk.server_error")] }]);
      }
      console.error("YGB info alınmadı", error);
    } finally {
      setSearchGbLoading(false);
    }
  };

  const selectContractNo = (selectedNo: string) => {
    const data     = currentYgbPage?.serverData;
    const contract = data?.contracts?.find((i: any) => i?.no === selectedNo);
    ygbForm.setFieldValue("contractDate", contract?.date ?? null);

    const mergedValues = {
      ...getCurrentYgbMergedValuesWithDerived(),
      contractDate: contract?.date ?? null,
      contractNo: selectedNo,
    };

    const pagesNow = ygbPages.map((p, idx) =>
      idx === ygbPageIndex - 1 ? { ...p, formValues: mergedValues } : p
    );

    persistCurrentYgbPageForm(mergedValues);
    recomputeSaveEnabled({ ygbPages: pagesNow });
  };

  const getAvansInfoByPin = async (pinArg?: string) => {
    try {
      setSearchGbLoading(true);
      setDgkErrorMessage(null);
      const pin = String(pinArg ?? getInfoFormAvans.getFieldValue("validationNumber") ?? "").trim();

      if (pin.length > 15) {
        getInfoFormAvans.setFields([{ name: "validationNumber", errors: [t("dgk.pin_invalid")] }]);
        return;
      }

      const payload  = { pinCode: pin, iban: props?.iban };
      const response = await GetInfByPinCode(payload);
      const data     = response?.data;

      if (!data) {
        getInfoFormAvans.setFields([{ name: "validationNumber", errors: [t("dgk.advance_code_not_found")] }]);
        return;
      }

      const lookups = await ensureLookupsLoaded();

      const normalizedContracts = (data.contracts || []).map((contract: any, idx: number) => {
        const relatedDetails = (data.details || []).filter(
          (d: any) =>
            String(d?.contract?.no || "") === String(contract?.no || "") &&
            String(d?.contract?.date || "") === String(contract?.date || "")
        );
        const firstDetail       = relatedDetails[0];
        const receiverCountryId =
          findCountryIdByName(lookups.countries, firstDetail?.country) ??
          (firstDetail?.countryCode ? String(firstDetail.countryCode) : undefined);

        return {
          id: `${contract?.no || "contract"}-${idx}`,
          contractNo: contract?.no || "",
          contractDate: contract?.date || "",
          accountNo: firstDetail?.accountNo || "",
          country: firstDetail?.country || "",
          countryCode: receiverCountryId,
          bank: firstDetail?.bank || "",
          details: relatedDetails,
        };
      });

      setAvansData(data);
      setAvansContracts(normalizedContracts);
      setOpenAvansRest(true);
      setAvansPage(1);

      suppressAutoConvertRef.current = true;

      const baseValues: Record<string, any> = {};
      normalizedContracts.forEach((item: any, idx: number) => {
        baseValues[`contNo-${idx}`]                 = item.contractNo;
        baseValues[`date-${idx}`]                   = item.contractDate;
        baseValues[`accountNo-${idx}`]              = item.accountNo;
        baseValues[`avansReceiverCountryId-${idx}`] = undefined;
        baseValues[`avansReceiver-${idx}`] =
        toConvert !== "RUB" ? String(props?.beneficiaryName ?? "").trim() : "";
        baseValues[`avansCurrencyType-${idx}`] =
        toFixedCurrencyOptions(toConvert, transferCurrency).length === 1
        ? toConvert
        : undefined;
        });

      const savedForm =
        dgkData?.type === "Avans" && dgkData?.pinCode === pin ? dgkData?.form || {} : {};

      const mergedValues = { ...baseValues, ...savedForm };
      normalizedContracts.forEach((item: any, idx: number) => {
        mergedValues[`accountNo-${idx}`] = avansAccountNoForDisplay(
          mergedValues[`avansCurrencyType-${idx}`],
          item.accountNo
        );
      });

      avansForm.resetFields();
      avansForm.setFieldsValue(mergedValues);

      setTimeout(async () => {
        suppressAutoConvertRef.current = false;
        await applyAvansConvertForValues(avansForm.getFieldsValue(true), 0);
        recomputeSaveEnabled();
      }, 0);

      getInfoFormAvans.setFieldsValue({ validationNumber: pin });
      getInfoFormAvans.setFields([{ name: "validationNumber", errors: [] }]);
      setDgkErrorMessage(null);
    } catch (error) {
      console.error("Avans info alınmadı", error);
      if (isDgkSearchUnavailableError(error)) {
        setDgkErrorMessage(pickDgkApiMessage(error));
        getInfoFormAvans.setFields([{ name: "validationNumber", errors: [] }]);
      } else {
        getInfoFormAvans.setFields([{ name: "validationNumber", errors: [t("dgk.server_error")] }]);
      }
    } finally {
      setSearchGbLoading(false);
    }
  };

  const getAvansInfo = async () => { await getAvansInfoByPin(); };

 const deleteCurrentYgbPage = () => {
  if (!currentYgbPage) return;

  const gbNoToDelete = ygbPages[ygbPages.length - 1]?.gbNo;
  

    setYgbPages((prev) => {
      const next = prev.filter((p) => p.gbNo !== gbNoToDelete);

      const nextStatuses = { ...ygbConvertStatus };
      delete nextStatuses[gbNoToDelete];
      setYgbConvertStatus(nextStatuses);

      if (next.length === 0) {
  dispatch(setDgkData(null));

  const fresh: AktPage = {
    key: `akt-${Date.now()}`,
    formValues: {
      actExchange: "",
      actExchangeAmountInAccount: null,
    },
  };

  suppressAutoConvertRef.current = true;

  aktMainForm.resetFields();
  aktExtraForm.resetFields();

  setAktPages([fresh]);
  setAktPageIndex(1);
  setAktConvertStatus({});
  setSaveEnabled(false);

  setTimeout(() => {
    suppressAutoConvertRef.current = false;
  }, 0);

  props?.setVisible(false);

  return [fresh];
}

      dispatch(setDgkData(buildYgbStoreDataFromPages(next)));
      const newIndex = Math.max(1, Math.min(ygbPageIndex - 1, next.length));
      setYgbPageIndex(newIndex);
      setTimeout(() => {
        loadYgbPageToForms(next[newIndex - 1]);
        recomputeSaveEnabled({ ygbPages: next, ygbConvertStatus: nextStatuses });
      }, 0);
           return next;
    });
    };

  const addAktPage = async () => {
    try {
      await Promise.all([aktMainForm.validateFields(), aktExtraForm.validateFields()]);
      const mergedValues = getCurrentAktMergedValuesWithDerived();
      const currentPages = aktPages.map((p, idx) =>
        idx === aktPageIndex - 1 ? { ...p, formValues: mergedValues } : p
      );
      persistCurrentAktPageForm(mergedValues);
      const newPage: AktPage = { key: `akt-${Date.now()}`, formValues: { actExchange: "", actExchangeAmountInAccount: null } };
      setAktPages((prev) => {
        const next = [...currentPages, newPage];
        setTimeout(() => {
          setAktPageIndex(next.length);
          loadAktPageToForms(newPage);
          recomputeSaveEnabled({ aktPages: next });
        }, 0);
        return next;
      });
    } catch {}
  };

  const deleteCurrentAktPage = () => {
    const page = currentAktPage;
    if (!page) return;
    
    setAktPages((prev) => {
      const next = prev.filter((p) => p.key !== page.key);

      const nextStatuses = { ...aktConvertStatus };
      delete nextStatuses[page.key];
      setAktConvertStatus(nextStatuses);

      if (next.length === 0) {
  dispatch(setDgkData(null));

  suppressAutoConvertRef.current = true;

  aktMainForm.resetFields();
  aktExtraForm.resetFields();

  setAktPages([]);
  setAktPageIndex(1);
  setAktConvertStatus({});
  setSaveEnabled(false);

  setTimeout(() => {
    suppressAutoConvertRef.current = false;
  }, 0);

  props?.setVisible(false);

  return [];
}

      dispatch(setDgkData(buildAktStoreDataFromPages(next)));
      const newIndex = Math.max(1, Math.min(aktPageIndex - 1, next.length));
      setAktPageIndex(newIndex);
      setTimeout(() => {
        loadAktPageToForms(next[newIndex - 1]);
        recomputeSaveEnabled({ aktPages: next, aktConvertStatus: nextStatuses });
      }, 0);
      return next;
        });
};


  const handleCloseX = () => { props?.setVisible(false); };

  const handleCancelWithConfirm = () => {
    openCancelConfirm(() => {
        dispatch(setDgkData(null));
        getInfoForm.resetFields();
        getInfoFormAvans.resetFields();
        ygbInfoForm.resetFields();
        ygbForm.resetFields();
        aktMainForm.resetFields();
        aktExtraForm.resetFields();
        avansForm.resetFields();
        setYgbPages([]);
        setYgbPageIndex(1);
        setOpenYgbRest(false);
        setOpenGbPayments(false);
        setYgbPaymentsPage(1);
        const freshAkt: AktPage = { key: `akt-${Date.now()}`, formValues: {} };
        setAktPages([freshAkt]);
        setAktPageIndex(1);
        setAvansData(undefined);
        setAvansContracts([]);
        // setOpenAvansRest(false);
        // setOpenAvansContract(false);
        // setOpenAvansGbList(false);
        setAvansPage(1);
        setSaveEnabled(false);
        if (props.transferKind === "AKT") {
        props?.setVisible(false);
        }
        setYgbConvertStatus({});
        setAktConvertStatus({});
        setAvansConvertStatus({});
    });
  };

  const deleteCurrentAvans = () => {
    const currentIndex = avansStart;
    if (!avansContracts?.length) return;

        const nextContracts = avansContracts.filter((_, idx) => idx !== currentIndex);
        const currentValues = avansForm.getFieldsValue(true);
        const nextFormValues: Record<string, any> = {};

        nextContracts.forEach((item: any, newIdx: number) => {
          const oldIdx = avansContracts.findIndex((c) => c.id === item.id);
          nextFormValues[`contNo-${newIdx}`]                 = currentValues[`contNo-${oldIdx}`];
          nextFormValues[`date-${newIdx}`]                   = currentValues[`date-${oldIdx}`];
          nextFormValues[`accountNo-${newIdx}`]              = currentValues[`accountNo-${oldIdx}`];
          nextFormValues[`avansReceiverCountryId-${newIdx}`] = currentValues[`avansReceiverCountryId-${oldIdx}`];
          nextFormValues[`avansReceiver-${newIdx}`]          = currentValues[`avansReceiver-${oldIdx}`];
          nextFormValues[`avansTransferredAmount-${newIdx}`] = currentValues[`avansTransferredAmount-${oldIdx}`];
          nextFormValues[`avansCurrencyType-${newIdx}`]      = currentValues[`avansCurrencyType-${oldIdx}`];
          nextFormValues[`avansExchange-${newIdx}`]          = currentValues[`avansExchange-${oldIdx}`];
          nextFormValues[`avansExchangeAmountInAccount-${newIdx}`] = currentValues[`avansExchangeAmountInAccount-${oldIdx}`];
        });

        const nextStatuses: Record<number, "idle" | "loading" | "success" | "error"> = {};
        nextContracts.forEach((item: any, newIdx: number) => {
          const oldIdx = avansContracts.findIndex((c) => c.id === item.id);
          nextStatuses[newIdx] = avansConvertStatus[oldIdx] || "idle";
        });

        if (nextContracts.length === 0) {
  dispatch(setDgkData(null));

  suppressAutoConvertRef.current = true;

  avansForm.resetFields();
  getInfoFormAvans.resetFields();

  setAvansData(undefined);
  setAvansContracts([]);
  setOpenAvansRest(false);
  setOpenAvansContract(false);
  setOpenAvansGbList(false);
  setAvansPage(1);
  setAvansConvertStatus({});
  setSaveEnabled(false);

  setTimeout(() => {
    suppressAutoConvertRef.current = false;
  }, 0);

  return;
}
        suppressAutoConvertRef.current = true;
        avansForm.resetFields();
        setAvansContracts(nextContracts);
        setAvansConvertStatus(nextStatuses);
        const newPage = Math.min(avansPage, nextContracts.length);
        setAvansPage(newPage);

              setTimeout(() => {
      avansForm.setFieldsValue(nextFormValues);
      dispatch(setDgkData(buildAvansStoreData(nextContracts, nextFormValues)));
      suppressAutoConvertRef.current = false;
      recomputeSaveEnabled({ avansConvertStatus: nextStatuses });
    }, 0);
};

  const computeAvansExchangeTotalFromValues = (values: any) => {
    let total = 0;
    for (let i = 0; i < (avansContracts?.length || 0); i++) {
      const rawAmount        = normalizeDecimal(values[`avansTransferredAmount-${i}`]);
      const selectedCurrency = String(values[`avansCurrencyType-${i}`] || "").trim();
      if (!selectedCurrency || !Number.isFinite(rawAmount)) continue;
      if (isSameCurrency(selectedCurrency, toConvert)) {
        total += rawAmount;
        continue;
      }
      const ex = String(values[`avansExchange-${i}`] || "").trim();
      const m  = ex.match(/=\s*([0-9]+(?:\.[0-9]+)?)\s*([A-Z]{3})$/);
      if (!m) continue;
      const amount = Number(m[1]);
      const cur    = m[2];
      if (isSameCurrency(cur, toConvert) && Number.isFinite(amount)) total += amount;
    }
    return total;
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (props.transferKind === "YGB") {
        const currentMerged = { ...currentYgbPage?.formValues, ...getCurrentYgbMergedValuesWithDerived() };
        const latestPages   = ygbPages.map((p, idx) =>
          idx === ygbPageIndex - 1 ? { ...p, formValues: currentMerged } : p
        );
        persistCurrentYgbPageForm(currentMerged);
        await ygbForm.validateFields();

        if (latestPages.every((p) => isSameCurrency(p.formValues?.currencyType, toConvert))) {
          const cross = validateYgbCrossRules(latestPages);
          if (cross.sumExceedsTransfer) {
            ygbForm.setFields([{ name: "transferredAmount", errors: [t("dgk.gb_transfer_exceed")] }]);
            return;
          }
          const perErr = cross.errorsByGbNo[currentYgbPage?.gbNo];
          if (perErr) {
            ygbForm.setFields([{ name: "transferredAmount", errors: [perErr] }]);
            return;
          }
        }

        const latestExchangeTotal = computeExchangeTotalFromPages(latestPages, "YGB");
        dispatch(
          setDgkData({
            type: "YGB",
            iban: props.iban,
            accountCurrency: toConvert,
            total_exchange_amount: Number(latestExchangeTotal.toFixed(2)),
            pages: latestPages.map((p) => ({
              gbNo: p.gbNo,
              summary: {
                name: p.serverData?.name,
                taxId: p.serverData?.taxId,
                receiverCountry: p.serverData?.receiverCountry,
                invoiceCost: p.serverData?.invoiceCost,
                invoiceCurrency: p.serverData?.invoiceCurrency,
                operationAmount: p.serverData?.operationAmount,
                operationCurrency: p.serverData?.operationCurrency,
                egbSum: p.serverData?.egbSum,
                egbCurrency: p.serverData?.egbCurrency,
                deliveryCondition: p.serverData?.deliveryCondition,
                bargainingFeature: p.serverData?.bargainingFeature,
                impDocDate: p.serverData?.impDocDate,
                impDocCost: p.serverData?.impDocCost,
                declarationTypeKey: p.serverData?.declarationTypeKey,
                senderOrExporter: p.serverData?.senderOrExporter,
                goodOfServiceInfo: p.serverData?.goodOfServiceInfo,
                note: p.serverData?.note,
                bankUserNumber: p.serverData?.bankUserNumber,
                pincode: p.serverData?.pincode ?? p.serverData?.pinCode,
              },
              form: p.formValues,
              filePath: p.serverData?.filePath,
              details: p.serverData?.details,
              contracts: p.serverData?.contracts,
            })),
          })
        );
        message.success(t("dgk.saved_successfully"));
        props.setVisible(false);
        return;
      }

      if (props.transferKind === "AKT") {
        const currentMerged = { ...currentAktPage?.formValues, ...getCurrentAktMergedValues() };
        const latestPages   = aktPages.map((p, idx) =>
          idx === aktPageIndex - 1 ? { ...p, formValues: currentMerged } : p
        );
        persistCurrentAktPageForm(currentMerged);
        await Promise.all([aktMainForm.validateFields(), aktExtraForm.validateFields()]);

        if (!validateAktUniqueness(latestPages)) {
          message.error(t("dgk.akt_unique_error"));
          return;
        }

        const latestExchangeTotal = computeExchangeTotalFromPages(latestPages, "AKT");
        if (Number.isFinite(transferAmount) && latestExchangeTotal > transferAmount) {
          aktMainForm.setFields([{ name: "actTransCost", errors: [t("dgk.akt_transfer_exceed")] }]);
          return;
        }
        dispatch(
          setDgkData({
            type: "AKT",
            iban: props.iban,
            accountCurrency: toConvert,
            taxId: dgkData?.taxId ?? null,
            total_exchange_amount: Number(latestExchangeTotal.toFixed(2)),
            pages: latestPages.map((p) => ({
              key: p.key,
              form: {
                ...p.formValues,
                countryName: countriesMap[p.formValues?.countryId]?.name ?? null,
              },
              summary: {
                actDocDate: p.formValues?.actDocDate,
                actDocNumber: p.formValues?.noActDocNumber ? null : p.formValues?.actDocNumber,
                actTransCost: p.formValues?.actTransCost,
                currencyType: p.formValues?.currencyType,
              },
            })),
          })
        );
        message.success(t("dgk.saved_successfully"));
        props.setVisible(false);
        return;
      }

      if (props.transferKind === "Avans") {
        await avansForm.validateFields();
        const values = avansForm.getFieldsValue(true);

        const avansAllSame = Array.from(
          { length: avansContracts?.length || 0 },
          (_, idx) => isSameCurrency(values[`avansCurrencyType-${idx}`], toConvert)
        ).every(Boolean);

        if (avansAllSame) {
          let sum = 0;
          for (let i = 0; i < (avansContracts?.length || 0); i++) {
            const a = normalizeDecimal(values[`avansTransferredAmount-${i}`]);
            if (Number.isFinite(a)) sum += a;
          }
          if (Number.isFinite(transferAmount) && sum > transferAmount) {
            avansForm.setFields([{ name: `avansTransferredAmount-${avansStart}`, errors: [t("dgk.advance_transfer_exceed")] }]);
            return;
          }
        }

        const latestExchangeTotal = computeAvansExchangeTotalFromValues(values);
        dispatch(
          setDgkData({
            type: "Avans",
            iban: props.iban,
            accountCurrency: toConvert,
            total_exchange_amount: Number(latestExchangeTotal.toFixed(2)),
            pinCode: avansData?.pinCode,
            summary: { pinCode: avansData?.pinCode, taxId: avansData?.taxId, name: avansData?.name },
            contracts: avansContracts || [],
            details: avansData?.details || [],
            gbList: avansData?.gbList || [],
            docFile: avansData?.docFile,
            form: values,
          })
        );
        message.success(t("dgk.saved_successfully"));
        props.setVisible(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const computeExchangeTotalFromPages = (pages: Array<{ formValues: any }>, type: "YGB" | "AKT") => {
    let total = 0;
    for (const p of pages) {
      const formValues       = p.formValues || {};
      const rawAmount        = type === "YGB" ? normalizeDecimal(formValues?.transferredAmount) : normalizeDecimal(formValues?.actTransCost);
      const selectedCurrency = String(formValues?.currencyType || "").trim();
      if (!selectedCurrency || !Number.isFinite(rawAmount)) continue;
      if (isSameCurrency(selectedCurrency, toConvert)) {
        total += rawAmount;
        continue;
      }
      const converted = type === "YGB" ? Number(formValues?.exchangeAmountInAccount) : Number(formValues?.actExchangeAmountInAccount);
      if (Number.isFinite(converted)) total += converted;
    }
    return total;
  };

  const ygbExchangeTotal = useMemo(
    () => computeExchangeTotalFromPages(ygbPages, "YGB"),
    [ygbPages, toConvert]
  );

  const aktExchangeTotal = useMemo(
    () => computeExchangeTotalFromPages(aktPages as any, "AKT"),
    [aktPages, toConvert]
  );

  const avansExchangeTotal = useMemo(() => {
    if (!avansData) return 0;
    return computeAvansExchangeTotalFromValues(avansWatchedValues || {});
  }, [avansWatchedValues, avansData, avansContracts.length, toConvert]);

  const isAnyConvertLoading =
    Object.values(ygbConvertStatus).includes("loading") ||
    Object.values(aktConvertStatus).includes("loading") ||
    Object.values(avansConvertStatus).includes("loading");

  const isDifferentCurrency = (selected?: string) => {
    const cur = String(selected ?? "").trim();
    return !!cur && !!toConvert && !isSameCurrency(cur, toConvert);
  };

  const getCurrMessageSection = () => {
    if (props.transferKind === "YGB") return "YGB";
    if (props.transferKind === "AKT") return t("dgk.akt");
    if (props.transferKind === "Avans") return t("dgk.advance");
    return "";
  };

  const buildYgbStoreDataFromPages = (pages: YgbPage[]) => ({
    type: "YGB",
    iban: props.iban,
    accountCurrency: toConvert,
    total_exchange_amount: Number(computeExchangeTotalFromPages(pages, "YGB").toFixed(2)),
    pages: pages.map((p) => ({
      gbNo: p.gbNo,
      summary: {
        name: p.serverData?.name,
        taxId: p.serverData?.taxId,
        receiverCountry: p.serverData?.receiverCountry,
        invoiceCost: p.serverData?.invoiceCost,
        invoiceCurrency: p.serverData?.invoiceCurrency,
        operationAmount: p.serverData?.operationAmount,
        operationCurrency: p.serverData?.operationCurrency,
        egbSum: p.serverData?.egbSum,
        egbCurrency: p.serverData?.egbCurrency,
        deliveryCondition: p.serverData?.deliveryCondition,
        bargainingFeature: p.serverData?.bargainingFeature,
        impDocDate: p.serverData?.impDocDate,
        impDocCost: p.serverData?.impDocCost,
        declarationTypeKey: p.serverData?.declarationTypeKey,
        senderOrExporter: p.serverData?.senderOrExporter,
        goodOfServiceInfo: p.serverData?.goodOfServiceInfo,
        note: p.serverData?.note,
        bankUserNumber: p.serverData?.bankUserNumber,
        pincode: p.serverData?.pincode ?? p.serverData?.pinCode,
      },
      form: p.formValues,
      filePath: p.serverData?.filePath,
      details: p.serverData?.details,
      contracts: p.serverData?.contracts,
    })),
  });

  const buildAktStoreDataFromPages = (pages: AktPage[]) => ({
    type: "AKT",
    iban: props.iban,
    accountCurrency: toConvert,
    total_exchange_amount: Number(computeExchangeTotalFromPages(pages as any, "AKT").toFixed(2)),
    pages: pages.map((p) => ({
      key: p.key,
      form: {
        ...p.formValues,
        countryName: countriesMap[p.formValues?.countryId]?.name ?? null,
      },
      summary: {
        actDocDate: p.formValues?.actDocDate,
        actDocNumber: p.formValues?.noActDocNumber ? null : p.formValues?.actDocNumber,
        actTransCost: p.formValues?.actTransCost,
        currencyType: p.formValues?.currencyType,
      },
    })),
  });

  const buildAvansStoreData = (contracts: any[], values: any) => ({
    type: "Avans",
    iban: props.iban,
    accountCurrency: toConvert,
    total_exchange_amount: Number(computeAvansExchangeTotalFromValues(values).toFixed(2)),
    pinCode: avansData?.pinCode,
    summary: { pinCode: avansData?.pinCode, taxId: avansData?.taxId, name: avansData?.name },
    contracts: contracts || [],
    details: avansData?.details || [],
    gbList: avansData?.gbList || [],
    docFile: avansData?.docFile,
    form: values,
  });

  const deleteCount =
  props.transferKind === "YGB"
    ? (dgkData?.type === "YGB" ? dgkData.pages?.length || 0 : 0)
    : props.transferKind === "AKT"
      ? (dgkData?.type === "AKT" ? dgkData.pages?.length || 0 : 0)
      : (dgkData?.type === "Avans" ? dgkData.contracts?.length || 0 : 0);

  return (
   <Modal
  destroyOnClose
  title={
    props.transferKind === "YGB"
      ? t("dgk.ygb_registration")
      : props.transferKind === "AKT"
        ? t("dgk.akt")
        : t("dgk.advance")
  }
  closeIcon={<ModalCloseIco />}
  centered
  open={props.visible}
  onCancel={handleCloseX}
  width={
    props.transferKind === "Avans"
      ? (openAvansRest ? 728 : 540)
      : openYgbRest || props.transferKind === "AKT"
        ? 614
        : 526
  }
  className={`manage_faster_menus swift_modal template_list_popup_modal template_modal dgkModal ${props?.className}`}
  keyboard={false}
  maskClosable={false}
  focusTriggerAfterClose={false}
  footer={false}
>
      {props.transferKind === "YGB" && (
        <>
          <div className="val">
            {dgkErrorMessage && <div className="dgk-error">{dgkErrorMessage}</div>}
            <div className="validationNumber ygbSearchRow">
              <p className="declaration ygbSearchLabel">
                {t("dgk.declaration_number")}
                <span className="tooltipWrapper">
                  <InfoIcoDgk />
                  <span className="tooltipBox">{t("dgk.multiple_declaration_info")}</span>
                </span>
              </p>

              <div className="validationSearchBlock">
                <Form form={getInfoForm} onFinish={getYgbInfo} className="validationSearchForm">
                  <div className="validationSearchRow">
                    <Form.Item
                      name="validationNumber"
                      className="validationSearchItem ygbSearchItem"
                      validateTrigger="onSubmit"
                      validateFirst
                      rules={[
                        { required: true, message: t("dgk.enter_declaration_number") },
                        {
                          validator: async (_, v) => {
                            const s = String(v || "").trim();
                            if (!s) return Promise.resolve();
                            if (!/^\d{1,14}$/.test(s)) return Promise.reject(new Error(t("dgk.max_14_digits")));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        maxLength={14}
                        type="number"
                        className="validationInput"
                        style={{ backgroundColor: "transparent" }}
                        onChange={() => getInfoForm.setFields([{ name: "validationNumber", errors: [] }])}
                      />
                    </Form.Item>

                    <Button htmlType="submit" loading={searchGbLoading}>
                      <SearchIcoDgk /> {t("dgk.search_add")}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>

            {openYgbRest && (
              <>
                <div className="line" style={{ margin: "0" }} />

                <div className="paymentContainer">
                  <p className="topTxt">{t("dgk.customs_general_info")}</p>
                  <div className="line" />

                  <div className="topBtns">
                    <Button
                      className="blue"
                      onClick={() => setOpenGbPayments(true)}
                      disabled={!activeYgbData?.details || activeYgbData?.details?.length === 0}
                    >
                      {t("dgk.gb_payments")}
                    </Button>

                    <Button
                      className="green"
                      onClick={() => {
                        if (!activeYgbData?.filePath) return;
                        const byteCharacters = atob(activeYgbData.filePath);
                        const byteNumbers    = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob      = new Blob([byteArray], { type: "application/zip" });
                        const url       = window.URL.createObjectURL(blob);
                        const a         = document.createElement("a");
                        a.href          = url;
                        a.download      = activeYgbData?.gbNo;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      }}
                    >
                      <DownloadIcoGb /> {t("dgk.e_gb")}
                    </Button>

                    <Button className="red deleteBtn" onClick={deleteCurrentYgbPage}>
                      <DeleteIcoGb />
                      <div className="customTooltip">{t("dgk.gb_delete_warning")}</div>
                    </Button>
                  </div>

                  <div className="form">
                    <Form form={ygbInfoForm}>
                      <div className="flex">
                        <Form.Item label={t("dgk.gb_number")} name="gbNo" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                          <Input readOnly maxLength={70} className="validationInput" />
                        </Form.Item>
                        <Form.Item label={t("dgk.legal_entity_name")} name="name" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                          <Input readOnly maxLength={70} className="validationInput ygbName" />
                        </Form.Item>
                      </div>
                      <div className="flex">
                        <Form.Item label={t("dgk.recipient_country")} name="receiverCountry" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                          <Input readOnly maxLength={70} className="validationInput" />
                        </Form.Item>
                        <Form.Item label={t("dgk.invoice_value")} name="invoiceCost" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                          <Input readOnly maxLength={70} className="validationInput" />
                        </Form.Item>
                      </div>
                      <div className="flex flex-last">
                        <Form.Item label={t("dgk.transaction_amount")} name="operationAmount" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                          <Input readOnly maxLength={70} className="validationInput" />
                        </Form.Item>
                        <div style={{ flex: 1 }}>
                          <Form.Item label={t("dgk.gb_remaining_amount")} name="egbSum" rules={[{ required: true, message: t("dgk.gb_not_found") }]}>
                            <Input readOnly maxLength={70} className="validationInput" />
                          </Form.Item>
                          {normalizeDecimal(activeYgbData?.egbSum) === 0 && (
                            <div style={{ marginTop: 15, color: "#015CD1", fontSize: 12, fontFamily: 'Gilroy-Regular' }}>
                              {t("dgk.gb_remaining_paid")}
                            </div>
                          )}
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>

                {true && (
                  <div className={`paymentContainer${normalizeDecimal(activeYgbData?.egbSum) === 0 ? ' paymentContainer--disabled' : ''}`}>
                    <p className="topTxt">{t("dgk.gb_contract_invoice_info")}</p>
                    <div className="line" />

                    <Form
                      form={ygbForm}
                      onValuesChange={(changed) => {
                        if (suppressAutoConvertRef.current) return;
                        const mergedValues = getCurrentYgbMergedValuesWithDerived();
                        const pagesNow     = ygbPages.map((p, idx) =>
                          idx === ygbPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                        );
                        persistCurrentYgbPageForm(mergedValues);
                        if ("transferredAmount" in changed || "currencyType" in changed) handleYgbConvert();
                        recomputeSaveEnabled({ ygbPages: pagesNow });
                      }}
                    >
                      <div className="flex">
                        <SelectField
                          labelItem={t("dgk.contract_number")}
                          placeholder={t("dgk.common.select")}
                          options={activeDocNoOptions}
                          name={"contractNo"}
                          onChange={(e: any) => selectContractNo(e)}
                          required={true}
                          className="gbNoInput"
                        />
                        <Form.Item
                          label={t("dgk.contract_date")}
                          name="contractDate"
                          rules={[{ required: true, message: t("dgk.common.fill_field") }]}
                        >
                          <Input readOnly maxLength={70} className="validationInput" />
                        </Form.Item>
                      </div>

                      <div className="flex">
                        <SelectField
  labelItem={t("dgk.recipient_country")}
  placeholder={t("dgk.common.select")}
  options={filteredCountryOptions}
  name={"receiverCountryId"}
  required={true}
  className="gbNoInput"
  showSearch
  filterOption={false}
  onSearch={handleSearchCountry}
  notFoundContent={t("notification.notFoundInfo")}
  keepSearchValueOnSelect
/>
                        <Form.Item
                          label={t("dgk.recipient")}
                          name="receiverName"
                          getValueFromEvent={sanitizeDgkText}
                          rules={[
                            { required: true, message: t("dgk.common.fill_field") },
                            {
                              validator: async (_, v) => {
                                if (!v) return Promise.resolve();
                                if (String(v).length > 300) return Promise.reject(new Error(t("dgk.common.max_300_chars")));
                                if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input style={{ backgroundColor: "white" }} maxLength={300} className="validationInput" />
                        </Form.Item>
                      </div>

                      <div className="flex flex-end">
                        <Form.Item
                          label={t("dgk.gb_transferred_amount")}
                          name="transferredAmount"
                          className="sideInput"
                          rules={[
                            { required: true, message: t("dgk.gb_transferred_amount_required") },
                            {
                              validator: async (_, v) => {
                                if (!v) return Promise.resolve();
                                if (!isMoney9x2(v)) return Promise.reject(new Error(t("dgk.money_9_2_error")));

                                const amt    = normalizeDecimal(v);
                                const egb    = normalizeDecimal(activeYgbData?.egbSum);
                                if (Number.isFinite(amt) && Number.isFinite(egb) && amt > egb) {
                                  return Promise.reject(new Error(t("dgk.gb_amount_vs_remaining_error")));
                                }

                                const cur = ygbForm.getFieldValue("currencyType");
                                if (isSameCurrency(cur, toConvert)) {
                                  const mergedValues = { ...getCurrentYgbMergedValuesWithDerived(), transferredAmount: v };
                                  const pages        = ygbPages.map((p, idx) =>
                                    idx === ygbPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                                  );
                                  const cross = validateYgbCrossRules(pages);
                                  if (cross.sumExceedsTransfer) return Promise.reject(new Error(t("dgk.gb_transfer_exceed")));
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            style={{ backgroundColor: "white" }}
                            maxLength={12}
                            className="validationInput"
                            inputMode="decimal"
                            onChange={(e) => {
                              const sanitized = sanitizeDecimalInput(e.target.value, 9, 2);
                              ygbForm.setFieldsValue({ transferredAmount: sanitized });
                              ygbForm.setFields([{ name: "transferredAmount", errors: [] }]);
                            }}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                            }}
                          />
                        </Form.Item>

                        <SelectField
                          labelItem={t("dgk.currency")}
                          placeholder={t("dgk.common.select")}
                          options={ygbCurrencyOptions}
                          name={"currencyType"}
                          required={true}
                          requiredMessage={t("dgk.currency_required")}
                          className="gbNoInput"
                        />

                        <Form.Item
                          label={t("dgk.exchange_by_account_currency")}
                          name="exchange"
                          className="sideInputRight"
                        >
                          <Input disabled maxLength={70} className="validationInput" />
                        </Form.Item>
                      </div>

                      <Form.Item shouldUpdate noStyle>
                        {() =>
                          isDifferentCurrency(ygbForm.getFieldValue("currencyType")) ? (
                            <div className="dgk-curr-message-row">
                              {t("dgk.currMessage", { section: getCurrMessageSection() })}
                            </div>
                          ) : null
                        }
                      </Form.Item>
                    </Form>
                  </div>
                )}

                {ygbPages.length > 0 && (
                  <Pagination
                    current={ygbPageIndex}
                    pageSize={1}
                    total={ygbPages.length}
                    onChange={(page) => {
                      const mergedValues = getCurrentYgbMergedValuesWithDerived();
                      const pagesNow     = ygbPages.map((p, idx) =>
                        idx === ygbPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                      );
                      persistCurrentYgbPageForm(mergedValues);
                      setYgbPageIndex(page);
                      loadYgbPageToForms(pagesNow[page - 1]);
                      setTimeout(async () => {
                        await applyYgbConvertForValues(pagesNow[page - 1]?.formValues, page - 1, pagesNow[page - 1]?.key);
                        recomputeSaveEnabled({ ygbPages: pagesNow });
                      }, 0);
                    }}
                    className="paginationDesign"
                  />
                )}

                <div className="bottom">
                  <div className="price">
                  <span>{t("dgk.total_exchange_amount_ygb")}</span>
                  <div>{(ygbExchangeTotal || 0).toFixed(2)} {toConvert || "—"}</div>
                  </div>
                  <div className="actionButtons">
                  <Button className="cancel" onClick={handleCancelWithConfirm}>Sil ({dgkData?.type === "YGB" ? (dgkData.pages?.length || 0) : 0})</Button>
                    <Button
                      className="add"
                      id="addDgk"
                      onClick={handleSave}
                      disabled={!saveEnabled || saving || isAnyConvertLoading}
                      loading={saving}
                    >
                      {t("dgk.save")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          <Modal
            title={t("dgk.gb_payments")}
            closeIcon={<ModalCloseIco />}
            centered
            open={openGbPayments}
            onCancel={() => setOpenGbPayments(false)}
            width={595}
            className={`manage_faster_menus swift_modal template_list_popup_modal template_modal gb_payments ${props?.className}`}
            keyboard={false}
            maskClosable={false}
            focusTriggerAfterClose={false}
            footer={false}
          >
            {currentPayments.map((item: any, idx: number) => (
              <React.Fragment key={item.id || idx}>
                <div className="topInfo">
                  <span>{t("dgk.payment_details_by_declaration", { number: item?.contract?.no })}</span>
                  <div className="line" />
                </div>
                <div className="midInfo">
                  <div className="first">
                    <div className="innerFirst"><span>{t("dgk.operation_date")}</span><p>{item.date}</p></div>
                    <div className="innerFirst"><span>{t("dgk.contract_number")}</span><p>{item?.contract?.no}</p></div>
                    <div className="innerFirst"><span>{t("dgk.contract_date")}</span><p>{item?.contract?.date}</p></div>
                  </div>
                  <div className="second">
                    <div className="innerFirst"><span>{t("dgk.bank_operation")}</span><p>BANK RESPUBLİKA ASC</p></div>
                    <div className="innerFirst"><span>{t("dgk.account_number")}</span><p>{item.accountNo}</p></div>
                  </div>
                  <div className="first">
                    <div className="innerFirst"><span>{t("dgk.gb_amount_sent")}</span><p>{item.paymentAmount} {item.paymentCurrency}</p></div>
                    <div className="innerFirst" id="noteLinee"><span>{t("dgk.recipient_country")}</span><p>{item.country}</p></div>
                    <div className="innerFirst"><span>{t("dgk.recipient")}</span><p>{item?.contract?.name || "–"}</p></div>
                  </div>
                  <div className="first" style={{ width: "100%" }}>
                    <div className="innerFirst" style={{ width: "100%" }}>
                      <span>{t("dgk.note")}</span>
                      <p className="classs" id="noteLine">{item.note}</p>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="line" style={{ marginTop: "0px" }} />
            <Pagination
              current={ygbPaymentsPage}
              pageSize={formsPerPage}
              total={ygbDetailsTotal}
              onChange={(page) => setYgbPaymentsPage(page)}
              style={{ marginTop: 16 }}
              className="paginationDesign"
            />
          </Modal>
        </>
      )}

      {props.transferKind === "AKT" && (
        <div className="val aktForm">
          <div className="paymentContainer">
            <div className="aktHead">
              <p className="topTxt">{t("dgk.general_information")}</p>
              <div className="topBtns">
                <Button className="blue" onClick={addAktPage}>
                  <PlusIcoGb /> {t("dgk.common.add")}
                </Button>
                <Button className="red deleteBtn" onClick={deleteCurrentAktPage}>
                  <DeleteIcoGb />
                  <div className="customTooltip">{t("dgk.current_akt_deleted")}</div>
                </Button>
              </div>
            </div>
            <div className="linee" />

            <div className="form">
              <Form
                form={aktMainForm}
                onValuesChange={(changed) => {
                  if (suppressAutoConvertRef.current) return;
                  // Merge over the persisted page values (base) so fields that
                  // live on aktExtraForm — or any field not currently touched —
                  // are preserved instead of being dropped from the page.
                  const allValues = getCurrentAktMergedValues();
                  const pagesNow     = aktPages.map((p, idx) =>
                    idx === aktPageIndex - 1 ? { ...p, formValues: allValues } : p
                  );
                  persistCurrentAktPageForm(allValues);
                  if ("actTransCost" in changed || "currencyType" in changed) handleAktConvert();

                  // The transferred amount may not exceed the document's total.
                  // Checked from both sides, so lowering the document amount also
                  // flags an amount that was already entered.
                  const docCost        = normalizeDecimal(allValues.actDocCost);
                  const transCost      = normalizeDecimal(allValues.actTransCost);
                  const exceedsDocCost =
                    Number.isFinite(docCost) && Number.isFinite(transCost) && transCost > docCost;

                  if (exceedsDocCost) {
                    aktMainForm.setFields([
                      { name: "actTransCost", errors: [t("dgk.akt_transfer_exceeds_doc_cost")] },
                    ]);
                  } else if (isSameCurrency(allValues.currencyType, toConvert)) {
                    const cross = validateAktTransferSum(pagesNow);
                    if (cross.sumExceedsTransfer) {
                      aktMainForm.setFields([{ name: "actTransCost", errors: [t("dgk.akt_transfer_exceed")] }]);
                    } else {
                      aktMainForm.setFields([{ name: "actTransCost", errors: [] }]);
                    }
                  }

                  recomputeSaveEnabled({ aktPages: pagesNow });
                }}
              >
                <div className="flex">
                  <Form.Item
                    label={t("dgk.act_date")}
                    name="actDocDate"
                    rules={[
                      { required: true, message: t("dgk.select_date") },
                      {
                        validator: async (_, v) => {
                          const noNum = aktMainForm.getFieldValue("noActDocNumber");
                          const num = aktMainForm.getFieldValue("actDocNumber");
                          
                          if (checkAktDuplicate(v, noNum, num)) {
                            return Promise.reject(new Error());
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      format="DD.MM.YYYY"
                      placeholder={t("dgk.common.select")}
                      className="validationInput dgkModalActDate"
                      suffixIcon={<DateIcon />}
                      onChange={(val) => {
                        aktMainForm.setFieldsValue({ actDocDate: val });
                        aktMainForm.validateFields(['actDocNumber']).catch(() => {});
                      }}
                    />
                  </Form.Item>

                  <div className="innerFlex">
                    <Form.Item
                      label={t("dgk.act_number")}
                      name="noActDocNumber"
                      valuePropName="checked"
                      initialValue={false}
                      className="leftInput"
                    >
                      <Checkbox
                        onChange={(e) => {
                          if (e.target.checked) aktMainForm.setFieldValue("actDocNumber", undefined);
                          const mergedValues = {
                            ...getCurrentAktMergedValues(),
                            noActDocNumber: e.target.checked,
                            actDocNumber: e.target.checked ? undefined : aktMainForm.getFieldValue("actDocNumber"),
                          };
                          const pagesNow = aktPages.map((p, idx) =>
                            idx === aktPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                          );
                          persistCurrentAktPageForm(mergedValues);
                          recomputeSaveEnabled({ aktPages: pagesNow });
                          
                          setTimeout(() => {
                            aktMainForm.validateFields(['actDocDate', 'actDocNumber']).catch(() => {});
                          }, 0);
                        }}
                      >
                        {t("dgk.none")}
                      </Checkbox>
                    </Form.Item>

                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const noNumber = aktMainForm.getFieldValue("noActDocNumber");
                        return (
                          <Form.Item
                            label=" "
                            name="actDocNumber"
                            className="rightInput"
                            rules={[
                              { required: !noNumber, message: t("dgk.enter_act_number") },
                              {
                                validator: async (_, v) => {
                                  if (!v && !noNumber) return Promise.resolve();
                                  if (v && String(v).length > 50) return Promise.reject(new Error(t("dgk.common.max_50_chars")));
                                  if (v && hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.forbidden_symbols")));

                                  const date = aktMainForm.getFieldValue("actDocDate");
                                  if (checkAktDuplicate(date, noNumber, v)) {
                                    return Promise.reject(new Error(t("dgk.akt_unique_error") || "Bu tarix və nömrəyə aid Akt artıq mövcuddur"));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Input
                              disabled={noNumber}
                              maxLength={50}
                              className="validationInput"
                              onChange={(e) => {
                                // Keep only English letters/digits (+ basic punctuation);
                                // strips non-English letters (ç ş ə …) and forbidden
                                // symbols as they are typed, instead of only flagging them.
                                const clean = sanitizeDgkText(e);
                                aktMainForm.setFieldsValue({ actDocNumber: clean });
                                aktMainForm.validateFields(['actDocDate']).catch(() => {});
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </div>
                </div>

                <div className="flex">
                  <Form.Item
                    label={t("dgk.total_document_amount")}
                    name="actDocCost"
                    className="akt-doc-cost-item"
                    rules={[{ required: true, message: t("dgk.common.enter") }]}
                  >
                    <Input
                      maxLength={12}
                      className="validationInput"
                      onChange={(e) => {
                        const sanitized = sanitizeDecimalInput(e.target.value, 9, 2);
                        aktMainForm.setFieldsValue({ actDocCost: sanitized });
                        aktMainForm.setFields([{ name: "actDocCost", errors: [] }]);
                      }}
                      onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t("dgk.transferred_amount")}
                    name="actTransCost"
                    className="akt-trans-cost-item"
                    // An amount can break several rules at once (over the document
                    // total and over the transfer amount). Without this antd shows
                    // every failing rule's message stacked, and the second one
                    // overlaps the field below.
                    validateFirst
                    rules={[
                      { required: true, message: t("dgk.akt_transferred_amount_required") },
                      {
                        validator: async (_, v) => {
                          if (!v) return Promise.resolve();
                          const docCost   = normalizeDecimal(aktMainForm.getFieldValue("actDocCost"));
                          const transCost = normalizeDecimal(v);
                          if (Number.isFinite(docCost) && Number.isFinite(transCost) && transCost > docCost) {
                            return Promise.reject(new Error(t("dgk.akt_transfer_exceeds_doc_cost")));
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: async (_, v) => {
                          if (!v) return Promise.resolve();
                          const cur = aktMainForm.getFieldValue("currencyType");
                          if (!isSameCurrency(cur, toConvert)) return Promise.resolve();
                          const mergedValues = { ...getCurrentAktMergedValues(), actTransCost: v };
                          const sum          = getAktTransferSum(aktPages, mergedValues);
                          if (Number.isFinite(transferAmount) && sum > transferAmount) {
                            return Promise.reject(new Error(t("dgk.akt_transfer_exceed")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      maxLength={12}
                      className="validationInput"
                      onChange={(e) => {
                        const sanitized = sanitizeDecimalInput(e.target.value, 9, 2);
                        aktMainForm.setFieldsValue({ actTransCost: sanitized });
                        aktMainForm.setFields([{ name: "actTransCost", errors: [] }]);
                      }}
                      onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                    />
                  </Form.Item>

                  <div className="akt-currency-item">
                    {/* Usually just the account currency, in which case this stays a
                        fixed, disabled field rather than a real dropdown. It only opens
                        up when the transfer is being sent in a different currency. */}
                    <SelectField
                      labelItem={t("dgk.currency")}
                      placeholder={t("dgk.common.select")}
                      options={aktCurrencyOptions}
                      name={"currencyType"}
                      required={true}
                      requiredMessage={t("dgk.currency_required")}
                      className="gbNoInput"
                      disabled={aktCurrencyOptions.length <= 1}
                    />
                  </div>
                </div>

                <div className="flex flex-last-akt dgk-convert-row">
                  <Form.Item
                    label={t("dgk.exchange_by_account_currency")}
                    name="actExchange"
                    className="akt-exchange-item"
                  >
                    <Input disabled maxLength={70} className="validationInput" />
                  </Form.Item>

                  <Form.Item shouldUpdate noStyle>
                    {() =>
                      isDifferentCurrency(aktMainForm.getFieldValue("currencyType")) ? (
                        <div className="dgk-curr-message-row">
                          {t("dgk.currMessage", { section: getCurrMessageSection() })}
                        </div>
                      ) : null
                    }
                  </Form.Item>
                </div>
              </Form>
            </div>
          </div>

          <div className="paymentContainer">
            <p className="topTxt">{t("dgk.sender_exporter")}</p>

            <Form
              form={aktExtraForm}
              onValuesChange={() => {
                if (suppressAutoConvertRef.current) return;
                const mergedValues = getCurrentAktMergedValues();
                const pagesNow     = aktPages.map((p, idx) =>
                  idx === aktPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                );
                persistCurrentAktPageForm(mergedValues);
                recomputeSaveEnabled({ aktPages: pagesNow });
              }}
            >
              <div className="flex">
                <Form.Item
                  label={t("dgk.act_recipient")}
                  name="actSender"
                  getValueFromEvent={sanitizeDgkText}
                  rules={[
                    { required: !aktServiceProviderValue, message: t("dgk.common.enter") },
                    {
                      validator: async (_, v) => {
                        if (aktServiceProviderValue) return Promise.resolve();
                        if (!v) return Promise.resolve();
                        if (String(v).length > 300) return Promise.reject(new Error(t("dgk.common.max_300_chars")));
                        if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input disabled={!!aktServiceProviderValue} maxLength={300} className="validationInput" />
                </Form.Item>

                <div className="innerFlex2">
                  <Form.Item
                    label={t("dgk.service_provider")}
                    className="leftInput"
                    name="actServiceProvider"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          aktExtraForm.setFieldsValue({ actSender: undefined });
                          aktExtraForm.setFields([{ name: "actSender", errors: [] }]);
                        } else {
                          aktExtraForm.setFieldsValue({ actServiceProviderName: undefined });
                          aktExtraForm.setFields([{ name: "actServiceProviderName", errors: [] }]);
                        }
                        const mergedValues = {
                          ...getCurrentAktMergedValuesWithDerived(),
                          actServiceProvider: checked,
                          actSender: checked ? undefined : aktExtraForm.getFieldValue("actSender"),
                          actServiceProviderName: checked ? aktExtraForm.getFieldValue("actServiceProviderName") : undefined,
                        };
                        const pagesNow = aktPages.map((p, idx) =>
                          idx === aktPageIndex - 1 ? { ...p, formValues: mergedValues } : p
                        );
                        persistCurrentAktPageForm(mergedValues);
                        recomputeSaveEnabled({ aktPages: pagesNow });
                      }}
                    >
                      {t("dgk.available")}
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    label=" "
                    name="actServiceProviderName"
                    className="rightInput"
                    getValueFromEvent={sanitizeDgkText}
                    rules={[
                      { required: !!aktServiceProviderValue, message: t("dgk.common.enter") },
                      {
                        validator: async (_, v) => {
                          if (!aktServiceProviderValue) return Promise.resolve();
                          if (!v) return Promise.resolve();
                          if (String(v).length > 300) return Promise.reject(new Error(t("dgk.common.max_300_chars")));
                          if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      disabled={!aktServiceProviderValue}
                      placeholder={t("dgk.service_provider_name")}
                      maxLength={300}
                      className="validationInput"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="flex firstLong">
                <Form.Item
                  label={t("dgk.goods_services_info")}
                  name="goodServiceInfo"
                  className="first"
                  getValueFromEvent={sanitizeDgkText}
                  style={{
                    width:"66%"
                  }}
                  rules={[
                    { required: true, message: t("dgk.common.enter") },
                    {
                      validator: async (_, v) => {
                        if (!v) return Promise.resolve();
                        if (String(v).length > 300) return Promise.reject(new Error(t("dgk.common.max_300_chars")));
                        if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input maxLength={300} className="validationInput" />
                </Form.Item>

                <SelectField
  labelItem={t("dgk.country")}
  placeholder={t("dgk.common.select")}
  options={filteredCountryOptions}
  name={"countryId"}
  required={true}
  className="gbNoInput"
  showSearch
  filterOption={false}
  onSearch={handleSearchCountry}
  notFoundContent={t("notification.notFoundInfo")}
  keepSearchValueOnSelect
/>
              </div>

              <div className="flex flex-end">
                <Form.Item
                  label={t("dgk.note")}
                  name="note"
                  className="fullText"
                  getValueFromEvent={sanitizeDgkText}
                  rules={[
                    {
                      validator: async (_, v) => {
                        if (!v) return Promise.resolve();
                        // maxLength={150} on the input already caps the length; here
                        // we only enforce the ENG-only + forbidden-symbol rules.
                        if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TextArea maxLength={150} placeholder={t("dgk.common.not_entered")} className="validationInput" />
                </Form.Item>
              </div>
            </Form>
          </div>

          {aktPages.length > 0 && (
            <Pagination
              current={aktPageIndex}
              pageSize={1}
              total={aktPages.length}
              onChange={(page) => {
                // Persist the leaving page over its existing values so extra-form
                // fields (and any untouched field) survive the page switch.
                const allValues = getCurrentAktMergedValuesWithDerived();
                const pagesNow     = aktPages.map((p, idx) =>
                  idx === aktPageIndex - 1 ? { ...p, formValues: allValues } : p
                );
                persistCurrentAktPageForm(allValues);
                setAktPageIndex(page);
                loadAktPageToForms(pagesNow[page - 1]);
                setTimeout(async () => {
                  await applyAktConvertForValues(pagesNow[page - 1]?.formValues, page - 1, pagesNow[page - 1]?.key);
                  recomputeSaveEnabled({ aktPages: pagesNow });
                }, 0);
              }}
              className="paginationDesign"
            />
          )}

          <div className="bottom">
            <div className="price">
            <span>{t("dgk.total_exchange_amount_akt")}</span>
            <div>{(aktExchangeTotal || 0).toFixed(2)} {toConvert || "—"}</div>
            </div>
            <div className="actionButtons">
             <Button className="cancel" onClick={handleCancelWithConfirm}>Sil ({dgkData?.type === "AKT" ? (dgkData.pages?.length || 0) : 0})</Button>
              <Button
                className="add"
                onClick={handleSave}
                id="addDgk"
                disabled={!saveEnabled || saving || isAnyConvertLoading}
                loading={saving}
              >
                {t("dgk.save")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {props.transferKind === "Avans" && (
        <div className="val2">
          {dgkErrorMessage && <div className="dgk-error">{dgkErrorMessage}</div>}
          <div className="validationNumber">
            <p className="declaration avansLabel">{t("dgk.advance_pin")}</p>
            <div className="validationSearchBlock">
              <Form form={getInfoFormAvans} onFinish={getAvansInfo} className="validationSearchForm">
                <div className="validationSearchRow validationSearchRowAvans">
                  <Form.Item
                    name="validationNumber"
                    className="validationSearchItem"
                    validateTrigger="onSubmit"
                    validateFirst
                    rules={[
                      { required: true, message: t("dgk.advance_code_not_found") },
                      {
                        validator: async (_, v) => {
                          const s = String(v || "").trim();
                          if (!s) return Promise.resolve();
                          return s.length <= 15
                            ? Promise.resolve()
                            : Promise.reject(new Error(t("dgk.pin_invalid")));
                        },
                      },
                    ]}
                  >
                    <Input
                      maxLength={15}
                      placeholder={t("dgk.common.enter")}
                      className="validationInput2"
                      onChange={() => getInfoFormAvans.setFields([{ name: "validationNumber", errors: [] }])}
                    />
                  </Form.Item>

                  <Button htmlType="submit" loading={searchGbLoading}>
                    <SearchIcoDgk /> {t("dgk.search_add")}
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {openAvansRest && avansData && (
            <>
              <div className="line" />

              <div className="contentBox">
                <div className="top">
                  <div className="first"><p>{t("dgk.advance_pin")}</p><span>{avansData?.pinCode}</span></div>
                  <div className="first"><p>{t("dgk.tin")}</p><span>{avansData?.taxId}</span></div>
                  <div className="firsttt"><p>{t("dgk.legal_individual_name")}</p><span>{avansData?.name}</span></div>
                </div>

                <div className="middleButtons">
                  <button className="second" onClick={() => setOpenAvansGbList(true)}>
                    {t("dgk.advance_contract_gb")} ({avansData?.gbList?.length ?? 0})
                  </button>
                  <button className="second" onClick={() => setOpenAvansContract(true)}>
                    {t("dgk.advance_contract_payments")} ({avansData?.details?.length ?? 0})
                  </button>
                </div>

                <div className="formBody">
                  <div className="top">
                    <h3>{t("dgk.new_advance_registration")}</h3>
                    <button
                      className="midBtn"
                      type="button"
                      onClick={() => downloadBase64Pdf(avansData?.filePath ?? avansData?.docFile, `${avansData?.pinCode || "advance"}.pdf`)}
                    >
                      <DownloadIcoGb /> {t("dgk.upload_document")}
                    </button>
                    <Button className="red deleteBtn" onClick={deleteCurrentAvans}>
                      <DeleteIcoGb />
                      <div className="customTooltip">{t("dgk.current_advance_deleted")}</div>
                    </Button>
                  </div>

                  <Form
                    form={avansForm}
                    className="formAvans"
                    onValuesChange={(changed) => {
                      if (suppressAutoConvertRef.current) return;
                      const i = avansStart;
                      if (`avansTransferredAmount-${i}` in changed || `avansCurrencyType-${i}` in changed) {
                        handleAvansConvert();
                      }
                      recomputeSaveEnabled();
                    }}
                  >
                    {currentAvansForms.map((detail: any, idx: number) => (
                      <div key={detail?.id ?? idx} className="avansInside">
                        <div className="flex">
                          <Form.Item label={t("dgk.contract_number")} name={`contNo-${avansStart + idx}`}>
                            <Input readOnly className="validationInput longNum" />
                          </Form.Item>
                          <Form.Item label={t("dgk.contract_date")} name={`date-${avansStart + idx}`}>
                            <Input readOnly className="validationInput shortDate" />
                          </Form.Item>
                          <Form.Item label={t("dgk.account_number")} name={`accountNo-${avansStart + idx}`}>
                            <Input readOnly className="validationInput longAcc" />
                          </Form.Item>
                        </div>

                        <div className="flex">
                          <SelectField
  labelItem={t("dgk.recipient_country")}
  placeholder={t("dgk.common.select")}
  options={filteredCountryOptions}
  name={`avansReceiverCountryId-${avansStart + idx}`}
  required={true}
  className="gbNoInput"
  showSearch
  filterOption={false}
  onSearch={handleSearchCountry}
  notFoundContent={t("notification.notFoundInfo")}
  keepSearchValueOnSelect
/>
                          <Form.Item
                            label={t("dgk.recipient")}
                            name={`avansReceiver-${avansStart + idx}`}
                            getValueFromEvent={sanitizeDgkText}
                            rules={[
                              { required: true, message: t("dgk.common.fill_field") },
                              {
                                validator: async (_, v) => {
                                  if (!v) return Promise.resolve();
                                  if (String(v).length > 300) return Promise.reject(new Error(t("dgk.common.max_300_chars")));
                                  if (!isEnglishText(v) || hasForbiddenSymbols(v)) return Promise.reject(new Error(t("dgk.eng_only_forbidden_symbols")));
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Input maxLength={300} className="validationInput" />
                          </Form.Item>
                        </div>

                        <div className="flex flex-end dgk-convert-row">
                          <Form.Item
                            label={t("dgk.gb_transferred_amount")}
                            name={`avansTransferredAmount-${avansStart + idx}`}
                            className="sideInput"
                            rules={[
                              { required: true, message: t("dgk.advance_amount_required") },
                              {
                                validator: async (_, v) => {
                                  if (!v) return Promise.resolve();
                                  const currentIndex = avansStart + idx;
                                  const cur          = avansForm.getFieldValue(`avansCurrencyType-${currentIndex}`);
                                  if (!isSameCurrency(cur, toConvert)) return Promise.resolve();

                                  const values = avansForm.getFieldsValue(true);
                                  let sum = 0;
                                  for (let i = 0; i < (avansContracts?.length || 0); i++) {
                                    const currentValue = i === currentIndex ? v : values[`avansTransferredAmount-${i}`];
                                    const amount       = normalizeDecimal(currentValue);
                                    if (Number.isFinite(amount)) sum += amount;
                                  }
                                  if (Number.isFinite(transferAmount) && sum > transferAmount) {
                                    return Promise.reject(new Error(t("dgk.advance_transfer_exceed")));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Input
                              className="validationInput"
                              maxLength={12}
                              inputMode="decimal"
                              onChange={(e) => {
                                const fieldName = `avansTransferredAmount-${avansStart + idx}`;
                                const sanitized = sanitizeDecimalInput(e.target.value, 9, 2);
                                avansForm.setFieldsValue({ [fieldName]: sanitized });
                                avansForm.setFields([{ name: fieldName, errors: [] }]);
                              }}
                              onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                            />
                          </Form.Item>

                          {/* Same rule as AKT: disabled while the account currency is the
                              only option, selectable once the transfer currency differs. */}
                          <SelectField
                            labelItem={t("dgk.currency")}
                            placeholder={t("dgk.common.select")}
                            options={avansCurrencyOptions}
                            name={`avansCurrencyType-${avansStart + idx}`}
                            required={true}
                            requiredMessage={t("dgk.currency_required")}
                            className="gbNoInput"
                            disabled={avansCurrencyOptions.length <= 1}
                          />

                          <Form.Item
                            label={t("dgk.exchange_by_account_currency")}
                            name={`avansExchange-${avansStart + idx}`}
                            className="sideInputRight"
                          >
                            <Input disabled className="validationInput" />
                          </Form.Item>

                          <Form.Item shouldUpdate noStyle>
                            {() =>
                              isDifferentCurrency(avansForm.getFieldValue(`avansCurrencyType-${avansStart + idx}`)) ? (
                                <div className="dgk-curr-message-row">
                                  {t("dgk.currMessage", { section: getCurrMessageSection() })}
                                </div>
                              ) : null
                            }
                          </Form.Item>
                        </div>
                      </div>
                    ))}
                  </Form>
                </div>
              </div>

              <Pagination
                current={avansPage}
                pageSize={formsPerPage}
                total={avansDetailsTotal}
                onChange={(page) => {
                  setAvansPage(page);
                  const values = avansForm.getFieldsValue(true);
                  setTimeout(() => { applyAvansConvertForValues(values, (page - 1) * formsPerPage); }, 0);
                  recomputeSaveEnabled();
                }}
                style={{ marginTop: 16 }}
                className="paginationDesign"
              />

              <div className="bottom">
              <div className="price">
              <span>{t("dgk.total_exchange_amount_avans")}</span>
              <div>{(avansExchangeTotal || 0).toFixed(2)} {toConvert || "—"}</div>
              </div>
                <div className="actionButtons">
                 <Button className="cancel" onClick={handleCancelWithConfirm}>Sil ({dgkData?.type === "Avans" ? (dgkData.contracts?.length || 0) : 0})</Button>
                  <Button
                    className="add"
                    id="addDgk"
                    onClick={handleSave}
                    disabled={!saveEnabled || saving || isAnyConvertLoading}
                    loading={saving}
                  >
                    {t("dgk.save")}
                  </Button>
                </div>
              </div>

              <Modal
                title={t("dgk.advance_contract_payments")}
                closeIcon={<ModalCloseIco />}
                centered
                open={openAvansContract}
                onCancel={() => setOpenAvansContract(false)}
                width={595}
                className={`manage_faster_menus swift_modal template_list_popup_modal template_modal propAvans ${props?.className}`}
                keyboard={false}
                maskClosable={false}
                focusTriggerAfterClose={false}
                footer={false}
              >
                <div className="topInfo" style={{ marginRight: "24px" }}>
                  <span>{t("dgk.payment_details")}</span>
                  <div className="line" style={{ marginRight: "24px" }} />
                </div>

                <div style={{ maxHeight: 310, overflowY: "auto", paddingRight: 4 }}>
                  {(avansData?.details || []).map((item: any, idx: number) => (
                    <div key={item?.id || idx} style={{ marginBottom: 20 }}>
                      <div className="midInfo midAvansCont">
                        <div className="first">
                          <div className="innerFirst"><span>{t("dgk.operation_date")}</span><p>{item?.date}</p></div>
                          <div className="innerFirst"><span>{t("dgk.bank_operation")}</span><p>BANK RESPUBLİKA ASC</p></div>
                        </div>
                        <div className="second">
                          <div className="innerFirst"><span>{t("dgk.contract_number")}</span><p>{item?.contract?.no}</p></div>
                          <div className="innerFirst"><span>{t("dgk.contract_date")}</span><p>{item?.contract?.date}</p></div>
                        </div>
                        <div className="first">
                          <div className="innerFirst"><span>{t("dgk.account_number")}</span><p>{item?.accountNo}</p></div>
                          <div className="innerFirst republic"><span>{t("dgk.recipient_country")}</span><p>{item?.country}</p></div>
                          <div className="innerFirst"><span>{t("dgk.recipient")}</span><p>{item?.bank || "–"}</p></div>
                        </div>
                        <div className="first">
                          <div className="innerFirst"><span>{t("dgk.sent_amount")}</span><p>{item?.paymentAmount} {item?.paymentCurrency}</p></div>
                        </div>
                        <div className="first" style={{ width: "100%" }}>
                          <div className="innerFirst" style={{ width: "100%" }}>
                            <span>{t("dgk.note")}</span>
                            <p className="classs">{item?.note}</p>
                          </div>
                        </div>
                      </div>
                      {idx !== (avansData?.details || []).length - 1 && <div className="line" />}
                    </div>
                  ))}
                </div>
                <div className="line" style={{ marginRight: "24px" }} />
              </Modal>

              <Modal
                title={t("dgk.advance_contract_gb")}
                closeIcon={<ModalCloseIco />}
                centered
                open={openAvansGbList}
                onCancel={() => setOpenAvansGbList(false)}
                width={827}
                className={`manage_faster_menus swift_modal template_list_popup_modal template_modal ${props?.className} avans-gb-modal`}
                keyboard={false}
                maskClosable={false}
                focusTriggerAfterClose={false}
                footer={false}
              >
                <table className="gb-table">
                  <thead>
                    <tr className="topHead">
                      <th style={{ paddingLeft: "0", width: "50px" }}>#</th>
                      <th>{t("dgk.declaration_number")}</th>
                      <th>{t("dgk.invoice_value")}</th>
                      <th>{t("dgk.tin")}</th>
                    </tr>
                  </thead>
                  <tbody className="midBody">
                    {avansData?.gbList?.map((e: any, index: number) => (
                      <tr key={e.gbNo || index} style={{ width: "100%", gap: "20px", borderBottom: "1px solid #D9E3EC" }}>
                        <td style={{ width: "50px" }}>{index + 1}</td>
                        <td>{e?.gbNo}</td>
                        <td>{e?.invoiceCost} {e?.invoiceCurrency}</td>
                        <td>{avansData?.taxId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Modal>
            </>
          )}
        </div>
      )}

      <Modal
        open={cancelConfirmAction !== null}
        title={t("dgk.attention")}
        onCancel={closeCancelConfirm}
        closeIcon={<ModalCloseIco />}
        footer={null}
        centered
        width={420}
        className="dgkCancelConfirm"
      >
        <div className="dgkCancelConfirm__body">
          <span className="dgkCancelConfirm__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#FFA800" />
              <path d="M12 6.75V13.25" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16.75" r="1.25" fill="white" />
            </svg>
          </span>
          <p className="dgkCancelConfirm__text">
          {props.transferKind === "YGB"
          ? `YGB qeydiyyatı üzrə daxil edilən ${deleteCount} bəyannamə məlumatını silmək istədiyinizə əminsinizmi?`
          : props.transferKind === "AKT"
          ? `AKT qeydiyyatı üzrə daxil edilən ${deleteCount} məlumatı silmək istədiyinizə əminsinizmi?`
          : `Avans qeydiyyatı üzrə daxil edilən ${deleteCount} məlumatı silmək istədiyinizə əminsinizmi?`}
        </p>
        </div>
        <div className="dgkCancelConfirm__footer">
          <Button className="dgkCancelConfirm__no" onClick={closeCancelConfirm}>
            {t("dgk.common.no")}
          </Button>
          <Button
            type="primary"
            className="dgkCancelConfirm__yes"
            onClick={() => {
              const action = cancelConfirmAction;
              closeCancelConfirm();
              action?.();
            }}
          >
            {t("dgk.common.yes")}
          </Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default DgkModal;